import * as THREE from "three";
import "./style.css";
import "./feel.css";
import { clamp, distanceSq, easeOutBack, easeOutCubic, inArc, lerp, mulberry32, normalize } from "./logic";
import { SKILLS, STAGES, UNIT_MAX_HP } from "./stages";
import { CombatRhythm, fanDirection } from "./combat-feel";
import { measureFootPivots } from "./sprite-anchors";
import { PIXEL_WORLD, HUMAN_CELL, MONSTER_CELL, BOSS_CELL, HORSE_CELL, displayGrid } from "./pixel-layout";
import { CardinalStick } from "./cardinal-stick";
import type { Effect, EnemyKind, PendingHit, Projectile, RuntimeMetrics, SkillId, SkillSpec, StageSpec, Unit, Vec2 } from "./types";

const blockBrowserGesture = (event: Event): void => event.preventDefault();
document.addEventListener("touchmove", blockBrowserGesture, { passive: false });
document.addEventListener("gesturestart", blockBrowserGesture, { passive: false });
document.addEventListener("gesturechange", blockBrowserGesture, { passive: false });
document.addEventListener("gestureend", blockBrowserGesture, { passive: false });
document.addEventListener("dblclick", blockBrowserGesture, { passive: false });
document.addEventListener("contextmenu", blockBrowserGesture);

async function clearLegacyWebState(): Promise<void> {
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map(async (registration) => {
      try { await registration.update(); } catch { /* stale worker may be offline */ }
      await registration.unregister();
    }));
  }
  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }
}

void clearLegacyWebState();

const root = document.querySelector<HTMLDivElement>("#render-root")!;
const snapPixel = (value: number): number => Math.round(value / PIXEL_WORLD) * PIXEL_WORLD;
const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: "high-performance" });
renderer.setPixelRatio(1);
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.setClearColor(0x7da65f, 1);
renderer.sortObjects = true;
root.appendChild(renderer.domElement);

const scene = new THREE.Scene();
const camera = new THREE.OrthographicCamera(-10, 10, 5.625, -5.625, 0.1, 100);
camera.position.set(0, 0, 20);
camera.lookAt(0, 0, 0);

const textureLoader = new THREE.TextureLoader();
const texturePaths = {
  hero: "/assets/units-sfc/hero-zunda-samurai-v11.png",
  swordsman: "/assets/units-sfc/enemy-axeman-v11.png",
  lancer: "/assets/units-sfc/enemy-lancer-v11.png",
  archer: "/assets/units-sfc/enemy-archer-v11.png",
  monster: "/assets/units-sfc/oni-boar-v11.png",
  mountedBoss: "/assets/units-sfc/mounted-warlord-v11.png",
  warhorse: "/assets/units-sfc/warhorse-v11.png",
  allyLancer: "/assets/units-sfc/ally-lancer-v11.png",
  allyShield: "/assets/units-sfc/ally-shield-v11.png",
  allyArcher: "/assets/units-sfc/ally-archer-v11.png",
  magicBurst: "/assets/vfx/magic-jade-thunder-v1.png",
  bladeFx: "/assets/vfx/blade-impact-v2.png",
  scenery: "/assets/battlefields/props-v1.png",
} as const;
const textures = {} as Record<keyof typeof texturePaths, THREE.Texture>;
const battlefieldPaths = [
  "/assets/battlefields/stage1-wetland.png",
  "/assets/battlefields/stage2-river.png",
  "/assets/battlefields/stage3-fortress.png",
  "/assets/battlefields/stage4-moonshrine.png",
] as const;
const battlefieldTextures: THREE.Texture[] = [];
const floorTextures: THREE.Texture[] = [];
const stageProps: Array<{ x: number; y: number; mesh: THREE.Mesh; supply: boolean; taken: boolean }> = [];
let suppliesCollected = 0;

const atlasVertex = `
  varying vec2 vUv;
  uniform vec4 atlasFrame;
  void main() {
    vUv = atlasFrame.xy + uv * atlasFrame.zw;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const atlasFragment = `
  varying vec2 vUv;
  uniform sampler2D atlas;
  uniform vec3 tint;
  uniform vec3 teamColor;
  uniform float teamMix;
  uniform vec3 outlineColor;
  uniform vec2 pixelStep;
  uniform float ambientLift;
  uniform float flash;
  uniform float opacity;
  void main() {
    vec4 texel = texture2D(atlas, vUv);
    if (texel.a < 0.5) discard;
    float luminance = dot(texel.rgb, vec3(0.299, 0.587, 0.114));
    vec3 factionBase = mix(texel.rgb, teamColor * (0.3 + luminance * 1.15), teamMix);
    vec3 color = mix(factionBase * tint + vec3(ambientLift), vec3(1.0, 0.97, 0.82), flash);
    gl_FragColor = vec4(color, texel.a * opacity);
    #include <colorspace_fragment>
  }
`;

const unitGeometry = new THREE.PlaneGeometry(1, 1);
const shadowGeometry = new THREE.CircleGeometry(0.5, 28);
const groundCanvas = document.createElement("canvas");
groundCanvas.width = 2048;
groundCanvas.height = 1280;
const groundTexture = new THREE.CanvasTexture(groundCanvas);
groundTexture.colorSpace = THREE.SRGBColorSpace;
groundTexture.magFilter = THREE.NearestFilter;
groundTexture.minFilter = THREE.NearestFilter;
groundTexture.generateMipmaps = false;
const ground = new THREE.Mesh(new THREE.PlaneGeometry(60, 38), new THREE.MeshBasicMaterial({ map: groundTexture }));
ground.position.z = -1;
scene.add(ground);

let currentStage: StageSpec = STAGES[0];
let seed = Number(new URLSearchParams(location.search).get("seed") ?? 4242);
let random = mulberry32(seed);
let units: Unit[] = [];
let projectiles: Projectile[] = [];
let effects: Effect[] = [];
let pendingHits: PendingHit[] = [];
let hero: Unit;
let nextId = 1;
let selectedStage = Number(new URLSearchParams(location.search).get("stage") ?? 1);
let running = false;
let ended = false;
let currentWaveIndex = 0;
let waveTransition = 0;
let elapsed = 0;
let timeLeft = 0;
let ko = 0;
let combo = 0;
let comboTimer = 0;
let spirit = 0;
let freeze = 0;
let shake = 0;
let flashOverlay = 0;
let lastFrame = performance.now();
let fpsAccumulator = 0;
let fpsFrames = 0;
let fpsTimer = 0;
let maxFrameMs = 0;
let lastVisibleCount = 0;
let activeLaunches = 0;
let lastAttackLaunched = 0;
let autoplayClock = 0;
let autoplayStep = -1;
let enemyMeleeBudget = 0;
let enemyRangedBudget = 0;
let zoomKick = 0;
let activeSparkEffects = 0;
let activeTrailEffects = 0;
let horsePickup: { x: number; y: number; mesh: THREE.Mesh; shadow: THREE.Mesh } | null = null;
let horseMount: THREE.Mesh | null = null;
let mountedTime = 0;
let horseSpawned = false;
let trampleCooldown = 0;
let clashState: { bossId: number; time: number; meter: number; required: number } | null = null;
const engagedMeleeIds = new Set<number>();
const engagedRangedIds = new Set<number>();
const MAX_ENGAGED_MELEE = 5;
const MAX_ENGAGED_RANGED = 4;
let announceTimer = 0;
let audioEnabled = true;
let stickPointer: number | null = null;
const stickVector = { x: 0, y: 0 };
const cardinalStick = new CardinalStick();
const actionPointerOwners = new Map<HTMLElement, number>();
let pointerMoveTarget: Vec2 | null = null;
let pointerAttackTargetId: number | null = null;
let primaryChain = 0;
let primaryChainTimer = 0;
let rhythm = new CombatRhythm();
let releasedCharge = 0;
let dodgeCount = 0;
let perfectDodgeCount = 0;
let counterCount = 0;
let paused = false;
let simulationAccumulator = 0;
let hudTick = 0;
let waveCenter: Vec2 = { x: 0, y: 0 };
let routRemaining = 0;
let escapedEnemies = 0;
let waveStartedAt = 0;
let reflectedArrows = 0;
let rewardChoice: "flow" | "guard" | "power" = "flow";
const runPerks = { flow: 0, guard: 0, power: 0 };
let lastBattleWon = false;
let objectiveTargetId: number | null = null;
let pointerMarker: THREE.Mesh | null = null;
let objectiveMarker: THREE.Mesh | null = null;
let reinforcementTimer = 0;
let reinforcementCount = 0;
let friendlyFlagHp = 100;
let friendlyFlagUnderAttack = false;
let friendlyFlagWarningTier = 4;
let friendlyFlagMarker: THREE.Mesh | null = null;
const keyState = new Set<string>();
const cooldowns = new Map<SkillId, number>(SKILLS.map((skill) => [skill.id, 0]));
const MUSOU_THRESHOLD = 100;
const MUSOU_SKILL: SkillSpec = {
  id: "area", key: "I", label: "I", title: "翠天無双", cooldown: 1.35,
  damage: 118, range: 8.1, launch: 33, vertical: 20.5, color: 0xf7ffac,
};
const ALLY_ARROW_SKILL: SkillSpec = {
  id: "long", key: "", label: "", title: "援護矢", cooldown: 2.2,
  damage: 7, range: 13, launch: 3.4, vertical: 3.2, color: 0x78e9ff,
};
const frameAspect = 16 / 9;
const cameraTarget = new THREE.Vector2();
const cameraPosition = new THREE.Vector2();

const metrics: RuntimeMetrics = {
  seed, stage: selectedStage, fps: 0, frameMs: 0, maxFrameMs: 0, visibleEnemies: 0, peakVisibleEnemies: 0,
  activeEnemies: 0, maxLaunchedAtOnce: 0, lastAttackLaunched: 0, ko: 0, combo: 0, drawCalls: 0, triangles: 0, errors: [],
  spirit: 0, musouTriggers: 0, lastAttackWasMusou: false,
  heroX: 0, heroY: 0, heroFacing: 0, heroFrameRow: 0, lastSkill: "none",
  skillTriggers: { weak: 0, mid: 0, long: 0, area: 0, engage: 0, disengage: 0 }, touchActions: 0, stickActive: false,
  effectCount: 0, sparkCount: 0, trailCount: 0, engagedMelee: 0, engagedRanged: 0, watchingEnemies: 0,
  enemyWindups: 0, enemyRetreating: 0, enemyAttackStarts: 0, enemyRetreatStarts: 0, peakEnemyWindups: 0, engagementLimit: 5,
  mountedSeconds: 0, bossHp: 0, clashMeter: 0, pendingHits: 0,
  heroHp: 0, heroInvulnerable: 0,
  pointerEvents: 0, pointerTargetX: 0, pointerTargetY: 0, pointerTargetKind: "none",
  reinforcementCount: 0, objectiveAlive: false, objectiveDistance: 0,
  friendlyFlagHp: 100, friendlyFlagUnderAttack: false, battleEnded: false,
  viewportWidth: 0, viewportHeight: 0, cameraHalfWidth: 0, cameraHalfHeight: 0,
  pixelPitchMin: 0, pixelPitchMax: 0, pixelPitchSpreadPct: 0,
};
window.__BLOWBACK_METRICS__ = metrics;
window.addEventListener("error", (event) => metrics.errors.push(event.message));
window.addEventListener("unhandledrejection", (event) => metrics.errors.push(String(event.reason)));

class SynthAudio {
  private context?: AudioContext;
  private master?: GainNode;
  private noise?: AudioBuffer;

  unlock(): void {
    if (!audioEnabled || !navigator.userActivation.hasBeenActive) return;
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = 0.22;
      this.master.connect(this.context.destination);
      const buffer = this.context.createBuffer(1, this.context.sampleRate, this.context.sampleRate);
      const data = buffer.getChannelData(0);
      const noiseRandom = mulberry32(91073);
      for (let i = 0; i < data.length; i += 1) data[i] = noiseRandom() * 2 - 1;
      this.noise = buffer;
    }
    void this.context.resume();
  }

  hit(power = 1): void {
    if (!this.context || !this.master || !this.noise || !audioEnabled) return;
    const now = this.context.currentTime;
    const source = this.context.createBufferSource();
    const filter = this.context.createBiquadFilter();
    const gain = this.context.createGain();
    source.buffer = this.noise;
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(700 + power * 520, now);
    filter.Q.value = 1.2;
    gain.gain.setValueAtTime(0.12 * power, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08 + power * 0.025);
    source.connect(filter).connect(gain).connect(this.master);
    source.start(now);
    source.stop(now + 0.16);
    this.tone(120 + power * 65, 0.07, "sawtooth", 0.06 * power);
  }

  slash(power = 1): void { this.tone(520 + power * 180, 0.09, "triangle", 0.06 * power, 120); }
  magic(): void {
    this.tone(180, 0.28, "sine", 0.055, 760);
    this.tone(620, 0.18, "triangle", 0.045, 1480);
    this.tone(1180, 0.09, "square", 0.026, 360);
  }
  musou(): void {
    this.tone(92, 0.22, "sawtooth", 0.11, 760);
    this.tone(310, 0.28, "square", 0.055, 1320);
    this.tone(1120, 0.16, "triangle", 0.07, 240);
  }
  dash(): void { this.tone(180, 0.16, "sawtooth", 0.08, 900); }
  arrow(): void { this.tone(880, 0.06, "triangle", 0.025, 520); }
  land(power = 1): void { this.tone(65, 0.1, "square", 0.035 * power); }

  private tone(frequency: number, duration: number, type: OscillatorType, volume: number, endFrequency?: number): void {
    if (!this.context || !this.master || !audioEnabled) return;
    const now = this.context.currentTime;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    if (endFrequency) oscillator.frequency.exponentialRampToValueAtTime(endFrequency, now + duration);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    oscillator.connect(gain).connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }
}
const audio = new SynthAudio();
const haptic = (pattern: number | number[]): void => { if (navigator.vibrate && navigator.userActivation.hasBeenActive) navigator.vibrate(pattern); };

function loadTexture(path: string): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    textureLoader.load(path, (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.magFilter = THREE.NearestFilter;
      texture.minFilter = THREE.NearestFilter;
      texture.generateMipmaps = false;
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      resolve(texture);
    }, undefined, reject);
  });
}

function makeAtlasMaterial(texture: THREE.Texture, tint: THREE.Color, outlineColor: THREE.Color, ambientLift: number): THREE.ShaderMaterial {
  const source = texture.image as { width: number; height: number };
  return new THREE.ShaderMaterial({
    uniforms: {
      atlas: { value: texture },
      atlasFrame: { value: new THREE.Vector4(0, 0, 0.2, 0.25) },
      tint: { value: tint },
      teamColor: { value: new THREE.Color(1, 1, 1) },
      teamMix: { value: 0 },
      outlineColor: { value: outlineColor },
      pixelStep: { value: new THREE.Vector2(1 / source.width, 1 / source.height) },
      ambientLift: { value: ambientLift },
      flash: { value: 0 }, opacity: { value: 1 },
    },
    vertexShader: atlasVertex,
    fragmentShader: atlasFragment,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    side: THREE.DoubleSide,
  });
}

function makeUnitMaterial(kind: Unit["kind"], team: Unit["team"], variant = 0): THREE.ShaderMaterial {
  const allyTints = [new THREE.Color(1.05, 1.12, 1.14), new THREE.Color(0.94, 1.08, 1.24), new THREE.Color(1.14, 1.08, 0.96)];
  const stageEnemyTints = [new THREE.Color(1.08, 1.02, 0.98), new THREE.Color(0.98, 1.03, 1.08), new THREE.Color(1.1, 0.98, 0.94), new THREE.Color(1.02, 0.97, 1.08)];
  const enemyTint = stageEnemyTints[clamp(currentStage.id - 1, 0, stageEnemyTints.length - 1)].clone().multiplyScalar(variant === 1 ? 0.92 : variant === 2 ? 1.08 : 1);
  const tint = kind === "hero" ? new THREE.Color(1.06, 1.08, 1.02) : team === "player" ? allyTints[variant % allyTints.length] : kind === "monster" ? new THREE.Color(1.08, 1.05, 1.05) : kind === "mountedBoss" ? new THREE.Color(1.08, 1.03, 1.03) : enemyTint;
  const outline = kind === "hero" ? new THREE.Color(0.08, 0.15, 0.06) : team === "player" ? new THREE.Color(0.035, 0.13, 0.16) : new THREE.Color(0.19, 0.045, 0.04);
  const textureKey: keyof typeof texturePaths = team === "player" && kind !== "hero"
    ? kind === "lancer" ? "allyLancer" : kind === "archer" ? "allyArcher" : "allyShield"
    : kind;
  const material = makeAtlasMaterial(textures[textureKey], tint, outline, kind === "hero" ? 0.035 : team === "player" ? 0.05 : 0.065);
  // Versioned actor files share one palette. Do not re-grade every faction/frame.
  material.uniforms.tint.value.setRGB(1, 1, 1);
  material.uniforms.ambientLift.value = 0;
  if (team === "player" && kind !== "hero") {
    material.uniforms.teamColor.value = new THREE.Color(0.3, 0.93, 0.98);
    material.uniforms.teamMix.value = 0;
  }
  return material;
}

function setAtlasGridFrame(material: THREE.ShaderMaterial, columns: number, rows: number, row: number, column: number, insetPixels = 0): void {
  const pixelStep = material.uniforms.pixelStep.value as THREE.Vector2;
  const insetX = pixelStep.x * insetPixels;
  const insetY = pixelStep.y * insetPixels;
  const width = 1 / columns;
  const height = 1 / rows;
  material.uniforms.atlasFrame.value.set(column * width + insetX, 1 - (row + 1) * height + insetY, width - insetX * 2, height - insetY * 2);
}

function setHorseFrame(material: THREE.ShaderMaterial, row: number, column: number): void {
  setAtlasGridFrame(material, 8, 4, row, column, 0);
}

function horseRowForFacing(angle: number): number {
  const vertical = Math.sin(angle);
  if (vertical > 0.55) return 3;
  if (vertical < -0.55) return 0;
  return Math.cos(angle) >= 0 ? 2 : 1;
}

function unitBottomPadding(unit: Unit, row: number, column: number): number {
  const texture = (unit.mesh.material as THREE.ShaderMaterial).uniforms.atlas.value as THREE.Texture;
  const pivots = texture.userData.footPivots as number[] | undefined;
  if (pivots) return pivots[row * atlasColumns(unit) + column];
  return 3 / logicalCellSize(unit.kind).height;
}

function horseBottomPadding(row: number, column: number): number {
  const pivots = textures.warhorse.userData.footPivots as number[] | undefined;
  if (pivots) return pivots[row * 8 + column];
  return 3 / HORSE_CELL.height;
}

function createHorseSprite(): THREE.Mesh {
  const material = makeAtlasMaterial(textures.warhorse, new THREE.Color(1.08, 1.12, 1.02), new THREE.Color(0.78, 1, 0.55), 0.035);
  setHorseFrame(material, 0, 0);
  const mesh = new THREE.Mesh(unitGeometry, material);
  mesh.scale.set(HORSE_CELL.width * PIXEL_WORLD, HORSE_CELL.height * PIXEL_WORLD, 1);
  mesh.renderOrder = 24;
  scene.add(mesh);
  return mesh;
}

function spriteSize(kind: Unit["kind"]): { width: number; height: number } {
  const cell = logicalCellSize(kind);
  return { width: cell.width * PIXEL_WORLD, height: cell.height * PIXEL_WORLD };
}

function logicalCellSize(kind: Unit["kind"]): { width: number; height: number } {
  if (kind === "mountedBoss") return BOSS_CELL;
  if (kind === "monster") return MONSTER_CELL;
  return HUMAN_CELL;
}

function shadowWidth(kind: Unit["kind"]): number {
  // Transparent atlas padding never changes contact shadows or gameplay radii.
  return (kind === "mountedBoss" ? 144 : kind === "monster" ? 96 : 64) * PIXEL_WORLD;
}

function createUnit(kind: Unit["kind"], x: number, y: number, team: Unit["team"] = kind === "hero" ? "player" : "enemy"): Unit {
  const id = nextId++;
  const variant = (id + currentStage.id + (team === "player" ? 1 : 0)) % 3;
  const size = spriteSize(kind);
  const material = makeUnitMaterial(kind, team, variant);
  const mesh = new THREE.Mesh(unitGeometry, material);
  mesh.scale.set(size.width, size.height, 1);
  mesh.renderOrder = kind === "hero" ? 30 : 5;
  scene.add(mesh);
  const shadowMaterial = new THREE.MeshBasicMaterial({ color: 0x1b2618, transparent: true, opacity: kind === "hero" ? 0.28 : 0.2, depthWrite: false });
  const shadow = new THREE.Mesh(shadowGeometry, shadowMaterial);
  shadow.scale.set(shadowWidth(kind) * (kind === "hero" ? 0.42 : 0.36), shadowWidth(kind) * (kind === "hero" ? 0.13 : 0.11), 1);
  shadow.position.z = 0.12;
  scene.add(shadow);
  const hp = UNIT_MAX_HP[kind];
  return {
    id, kind, team, x, y, vx: 0, vy: 0, air: 0, vz: 0, hp, maxHp: hp,
    radius: kind === "mountedBoss" ? 1.15 : kind === "monster" ? 0.85 : kind === "hero" ? 0.55 : 0.56,
    state: "idle", facing: kind === "hero" ? 0 : Math.PI, stateTime: 0, attackCooldown: random() * 1.3,
    rangedCooldown: 1 + random() * 2, flash: 0, invulnerable: 0,
    launchResistance: kind === "mountedBoss" ? 0.76 : kind === "monster" ? 0.48 : kind === "lancer" ? 0.14 : 0,
    mesh, shadow, seed: random(), aiPhase: random() * Math.PI * 2,
    aiMode: "watch", aiTimer: 0, variant,
    slamPower: 0, chargePower: 0,
  };
}

function removeUnitVisual(unit: Unit): void {
  scene.remove(unit.mesh, unit.shadow);
  (unit.mesh.material as THREE.Material).dispose();
  (unit.shadow.material as THREE.Material).dispose();
}

function drawGround(stage: StageSpec): void {
  const context = groundCanvas.getContext("2d")!;
  const [deep, middle, grass, accent] = stage.palette;
  const gradient = context.createRadialGradient(1024, 610, 60, 1024, 610, 1180);
  gradient.addColorStop(0, grass);
  gradient.addColorStop(0.45, middle);
  gradient.addColorStop(1, deep);
  context.fillStyle = gradient;
  context.fillRect(0, 0, groundCanvas.width, groundCanvas.height);
  const localRandom = mulberry32(1000 + stage.id * 97);
  for (let i = 0; i < 900; i += 1) {
    const x = localRandom() * groundCanvas.width;
    const y = localRandom() * groundCanvas.height;
    const lengthValue = 4 + localRandom() * 18;
    context.strokeStyle = localRandom() > 0.82 ? `${accent}35` : `${deep}44`;
    context.lineWidth = 1 + localRandom() * 2;
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + (localRandom() - 0.5) * 8, y - lengthValue);
    context.stroke();
  }
  context.globalAlpha = 0.25;
  context.strokeStyle = accent;
  context.lineWidth = 8;
  context.beginPath();
  if (stage.id === 2) {
    context.moveTo(0, 260); context.bezierCurveTo(620, 80, 1320, 420, 2048, 190);
    context.moveTo(0, 1000); context.bezierCurveTo(650, 820, 1450, 1160, 2048, 850);
  } else if (stage.id === 3) {
    for (let x = 160; x < 2000; x += 300) { context.moveTo(x, 0); context.lineTo(x - 120, 1280); }
  } else if (stage.id === 4) {
    context.arc(1024, 640, 350, 0, Math.PI * 2);
    context.moveTo(1024, 180); context.lineTo(1024, 1100);
    context.moveTo(560, 640); context.lineTo(1488, 640);
  } else {
    context.moveTo(0, 630); context.bezierCurveTo(500, 520, 1500, 760, 2048, 600);
  }
  context.stroke();
  context.globalAlpha = 1;
  groundTexture.needsUpdate = true;
  const groundMaterial = ground.material as THREE.MeshBasicMaterial;
  groundMaterial.map = battlefieldTextures[stage.id - 1] ?? groundTexture;
  const floor = floorTextures[stage.id - 1];
  if (floor) {
    floor.wrapS = THREE.RepeatWrapping; floor.wrapT = THREE.RepeatWrapping;
    floor.repeat.set(stage.worldWidth / (64 * PIXEL_WORLD), stage.worldHeight / (64 * PIXEL_WORLD));
    groundMaterial.map = floor;
  }
  groundMaterial.color.setHex(0xffffff);
  groundMaterial.needsUpdate = true;
  ground.scale.set(stage.worldWidth / 60, stage.worldHeight / 38, 1);
}

function atlasColumns(unit: Unit): number {
  return unit.kind === "monster" || unit.kind === "mountedBoss" ? 8 : 10;
}

function atlasFrame(unit: Unit): { col: number; row: number; flip: number } {
  const isMonster = unit.kind === "monster";
  const isMountedBoss = unit.kind === "mountedBoss";
  const cols = atlasColumns(unit);
  let col = Math.floor(elapsed * 2.5 + unit.seed * 2) % 2;
  if (unit.state === "guard") col = unit.kind === "hero" ? 8 : 6;
  else if (unit.state === "attack") {
    const sequence = unit.kind === "hero" ? [8, 9, 9, 8] : cols === 10 ? [6, 7, 8, 9] : [6, 7];
    col = sequence[Math.min(sequence.length - 1, Math.floor(unit.stateTime / 0.075))];
  }
  else if (unit.state === "walk") {
    const walkCycle = unit.kind === "hero" ? [2, 3, 4, 5, 6, 7] : [2, 3, 4, 5];
    col = walkCycle[Math.floor(elapsed * 12 + unit.seed * 4) % walkCycle.length];
  } else if (unit.state === "launched" || unit.state === "down" || unit.state === "dead") col = cols - 1;
  let angle = unit.facing;
  while (angle < -Math.PI) angle += Math.PI * 2;
  while (angle > Math.PI) angle -= Math.PI * 2;
  const facingDown = Math.sin(angle) < -0.55;
  const facingUp = Math.sin(angle) > 0.55;
  let row = 0;
  let flip = 1;
  if (facingUp) row = isMonster ? 2 : 3;
  else if (!facingDown) {
    const facingRight = Math.cos(angle) >= 0;
    row = isMonster || isMountedBoss ? (facingRight ? 2 : 1) : facingRight ? 2 : 1;
    if (isMonster && !facingRight) flip = -1;
  }
  return { col: clamp(col, 0, cols - 1), row, flip };
}

function updateUnitVisual(unit: Unit): void {
  const material = unit.mesh.material as THREE.ShaderMaterial;
  const isMonster = unit.kind === "monster";
  const cols = atlasColumns(unit);
  const rows = isMonster ? 3 : 4;
  const frame = atlasFrame(unit);
  setAtlasGridFrame(material, cols, rows, frame.row, frame.col);
  material.uniforms.flash.value = unit.state === "stunned" ? 0.2 + Math.abs(Math.sin(unit.stateTime * 22)) * 0.2 : clamp(unit.flash * 4, 0, 0.55);
  const invulnerableBlink = 1;
  material.uniforms.opacity.value = unit.state === "dead" ? clamp(1 - (unit.stateTime - 0.45) * 2.3, 0, 1) : invulnerableBlink;
  const size = spriteSize(unit.kind);
  const walkBob = 0;
  const squash = 1;
  const braceWidth = 1;
  if (unit.state !== "launched") {
    unit.mesh.rotation.z = 0;
  }
  unit.mesh.scale.set(size.width * frame.flip * braceWidth, size.height * squash, 1);
  const mountOffset = unit.kind === "hero" && mountedTime > 0 ? 0.72 : 0;
  const bottomPadding = unitBottomPadding(unit, frame.row, frame.col) * size.height * squash;
  unit.mesh.position.set(snapPixel(unit.x), snapPixel(unit.y + unit.air + size.height * squash * 0.5 - bottomPadding + walkBob + mountOffset), 2.5 - unit.y * 0.003 + unit.air * 0.02 + mountOffset * 0.02);
  unit.mesh.renderOrder = 1000 - Math.round(unit.y * 48);
  unit.shadow.position.set(snapPixel(unit.x), snapPixel(unit.y), 0.15 - unit.y * 0.001);
  const shadowScale = clamp(1 - unit.air * 0.08, 0.38, 1);
  const mountedShadowX = unit.kind === "hero" && mountedTime > 0 ? 0.78 : unit.kind === "hero" ? 0.42 : 0.36;
  const mountedShadowY = unit.kind === "hero" && mountedTime > 0 ? 0.22 : unit.kind === "hero" ? 0.13 : 0.11;
  unit.shadow.scale.set(shadowWidth(unit.kind) * mountedShadowX * shadowScale, shadowWidth(unit.kind) * mountedShadowY * shadowScale, 1);
  (unit.shadow.material as THREE.MeshBasicMaterial).opacity = (unit.kind === "hero" ? 0.28 : unit.id === objectiveTargetId ? 0.42 : 0.2) * shadowScale;
}

function addSlash(x: number, y: number, angle: number, radius: number, color: number, opacity = 0.82, delay = 0, life = 0.14): void {
  const tint = new THREE.Color(color).lerp(new THREE.Color(0xffffff), 0.8);
  const material = makeAtlasMaterial(textures.bladeFx, tint, new THREE.Color(0), 0);
  setAtlasGridFrame(material, 4, 2, 0, 0);
  material.uniforms.opacity.value = opacity;
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(128 * PIXEL_WORLD, 128 * PIXEL_WORLD), material);
  mesh.position.set(snapPixel(x + Math.cos(angle) * Math.max(0, radius - 1.5) * 0.35), snapPixel(y + 0.38), 5);
  mesh.renderOrder = 500;
  mesh.rotation.z = angle;
  mesh.visible = delay <= 0;
  scene.add(mesh);
  effects.push({ mesh, age: -delay, life: Math.max(0.18, life), kind: "blade", x, y, scaleFrom: 1, scaleTo: 1, opacity });
}

function addSpark(x: number, y: number, color: number, amount: number): void {
  const allowed = Math.min(amount, Math.max(0, 18 - activeSparkEffects));
  for (let i = 0; i < allowed; i += 1) {
    const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1, depthWrite: false, blending: THREE.AdditiveBlending });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(0.05 + random() * 0.06, 0.2 + random() * 0.28), material);
    mesh.position.set(x + (random() - 0.5) * 0.5, y + 0.5 + random() * 0.8, 6);
    mesh.rotation.z = random() * Math.PI * 2;
    scene.add(mesh);
    effects.push({ mesh, age: -random() * 0.025, life: 0.14 + random() * 0.1, kind: "spark", x: mesh.position.x, y: mesh.position.y, scaleFrom: 1, scaleTo: 0.05 });
    activeSparkEffects += 1;
  }
}

function addImpactStar(x: number, y: number, color: number, power: number): void {
  const material = makeAtlasMaterial(textures.bladeFx, new THREE.Color(color).lerp(new THREE.Color(0xffffff), 0.8), new THREE.Color(0), 0);
  setAtlasGridFrame(material, 4, 2, 1, 0);
  const size = (power > 1.7 ? 80 : 56) * PIXEL_WORLD;
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), material);
  mesh.position.set(x, y + 0.5, 6.2);
  mesh.renderOrder = 2201;
  scene.add(mesh);
  effects.push({ mesh, age: 0, life: 0.16, kind: "hitSprite", x, y, scaleFrom: 1, scaleTo: 1 });
}

function addEnemyTelegraph(unit: Unit, direction: Vec2, duration: number): void {
  const length = unit.kind === "archer" ? 6.8 : unit.kind === "lancer" ? 4.2 : unit.kind === "monster" || unit.kind === "mountedBoss" ? 5 : 2.1;
  const material = new THREE.MeshBasicMaterial({ color: unit.kind === "archer" ? 0xec905b : 0xe96667, transparent: true, opacity: 0.28, depthWrite: false });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(length, unit.kind === "archer" ? 0.09 : 0.34), material);
  mesh.position.set(unit.x + direction.x * length / 2, unit.y + direction.y * length / 2, 0.6);
  mesh.rotation.z = Math.atan2(direction.y, direction.x);
  scene.add(mesh);
  effects.push({ mesh, age: 0, life: duration, kind: "telegraph", ownerId: unit.id, x: unit.x, y: unit.y, scaleFrom: 1, scaleTo: 1 });
}

function addMagicBurst(x: number, y: number): void {
  const material = makeAtlasMaterial(textures.magicBurst, new THREE.Color(1.08, 1.15, 1.12), new THREE.Color(0.02, 0.18, 0.16), 0.04);
  material.blending = THREE.NormalBlending;
  setAtlasGridFrame(material, 4, 1, 0, 0);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(128 * PIXEL_WORLD, 128 * PIXEL_WORLD), material);
  mesh.position.set(x, y + 0.62, 6.05);
  mesh.renderOrder = 34;
  scene.add(mesh);
  effects.push({ mesh, age: 0, life: 0.52, kind: "magic", x, y, scaleFrom: 0.72, scaleTo: 1.06, opacity: 1 });
}

function addAirTrail(unit: Unit): void {
  if (activeTrailEffects >= 24) return;
  const color = unit.kind === "hero" ? 0xbfff6a : 0xff5b4b;
  const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.58, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  const lengthValue = clamp(Math.hypot(unit.vx, unit.vy) * 0.12, 0.35, 1.45);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(lengthValue, 0.09), material);
  mesh.position.set(unit.x - unit.vx * 0.025, unit.y + unit.air + 0.72, 4.7);
  mesh.rotation.z = Math.atan2(unit.vy, unit.vx);
  scene.add(mesh);
  effects.push({ mesh, age: 0, life: 0.2, kind: "trail", x: unit.x, y: unit.y, scaleFrom: 1, scaleTo: 0.15 });
  activeTrailEffects += 1;
}

function clearSceneObjects(): void {
  clearStageProps();
  if (pointerMarker) {
    scene.remove(pointerMarker);
    (pointerMarker.material as THREE.Material).dispose();
    pointerMarker.geometry.dispose();
    pointerMarker = null;
  }
  if (objectiveMarker) {
    scene.remove(objectiveMarker);
    (objectiveMarker.material as THREE.Material).dispose();
    objectiveMarker.geometry.dispose();
    objectiveMarker = null;
  }
  if (friendlyFlagMarker) {
    scene.remove(friendlyFlagMarker);
    (friendlyFlagMarker.material as THREE.Material).dispose();
    friendlyFlagMarker.geometry.dispose();
    friendlyFlagMarker = null;
  }
  if (horsePickup) {
    scene.remove(horsePickup.mesh, horsePickup.shadow);
    (horsePickup.mesh.material as THREE.Material).dispose();
    (horsePickup.shadow.material as THREE.Material).dispose();
    horsePickup = null;
  }
  if (horseMount) {
    scene.remove(horseMount);
    (horseMount.material as THREE.Material).dispose();
    horseMount = null;
  }
  units.forEach(removeUnitVisual);
  units = [];
  for (const projectile of projectiles) {
    scene.remove(projectile.mesh);
    (projectile.mesh.material as THREE.Material).dispose();
    projectile.mesh.geometry.dispose();
  }
  projectiles = [];
  pendingHits = [];
  for (const effect of effects) {
    scene.remove(effect.mesh);
    const mesh = effect.mesh as THREE.Mesh;
    if (mesh.material) (mesh.material as THREE.Material).dispose();
    if (mesh.geometry) mesh.geometry.dispose();
  }
  effects = [];
  activeSparkEffects = 0;
  activeTrailEffects = 0;
  engagedMeleeIds.clear();
  engagedRangedIds.clear();
  mountedTime = 0;
  horseSpawned = false;
  trampleCooldown = 0;
  clashState = null;
  updateClashPanel();
}

function objectivePosition(): Vec2 {
  return clampToPlayable({ x: waveCenter.x + (currentWaveIndex % 2 ? -1.4 : 0.9), y: waveCenter.y + 3.6 });
}

function friendlyFlagPosition(): Vec2 {
  return clampToPlayable({ x: waveCenter.x - 0.85, y: waveCenter.y - 2.65 });
}

function isPortraitBattle(): boolean {
  return window.innerWidth / Math.max(1, window.innerHeight) < 0.72;
}

function cardinalDirection(direction: Vec2): Vec2 {
  if (Math.abs(direction.x) >= Math.abs(direction.y)) return { x: Math.sign(direction.x), y: 0 };
  return { x: 0, y: Math.sign(direction.y) };
}

function moveCardinal(unit: Unit, direction: Vec2, speed: number, dt: number): void {
  const step = cardinalDirection(direction);
  unit.x += step.x * speed * dt;
  unit.y += step.y * speed * dt;
  if (step.x !== 0 || step.y !== 0) unit.facing = Math.atan2(step.y, step.x);
}

function meleeEngagementLimit(): number {
  const phase = elapsed % 8;
  if (phase >= 2.8 && phase < 4.25) return 7;
  if (phase >= 6.2) return 3;
  return MAX_ENGAGED_MELEE;
}

function clampToPlayable(point: Vec2): Vec2 {
  const halfWidth = currentStage.worldWidth * 0.5;
  const halfHeight = currentStage.worldHeight * 0.5;
  let x = point.x;
  let y = point.y;
  if (currentStage.id === 1) {
    x = clamp(x, -halfWidth * 0.68, halfWidth * 0.68);
    y = clamp(y, -halfHeight * 0.62, halfHeight * 0.62);
  } else if (currentStage.id === 2) {
    const maxX = halfWidth * 0.86;
    const maxY = halfHeight * 0.84;
    const verticalHalfWidth = halfWidth * 0.15;
    const horizontalHalfHeight = halfHeight * 0.14;
    x = clamp(x, -maxX, maxX);
    y = clamp(y, -maxY, maxY);
    if (Math.abs(x) > verticalHalfWidth && Math.abs(y) > horizontalHalfHeight) {
      const xOverflow = Math.abs(x) - verticalHalfWidth;
      const yOverflow = Math.abs(y) - horizontalHalfHeight;
      if (xOverflow < yOverflow) x = Math.sign(x) * verticalHalfWidth;
      else y = Math.sign(y) * horizontalHalfHeight;
    }
  } else if (currentStage.id === 3) {
    x = clamp(x, -halfWidth * 0.78, halfWidth * 0.78);
    y = clamp(y, -halfHeight * 0.68, halfHeight * 0.68);
  } else {
    const maxX = halfWidth * 0.58;
    const maxY = halfHeight * 0.46;
    x = clamp(x, -maxX, maxX);
    y = clamp(y, -maxY, maxY);
    const octagonal = Math.abs(x) / maxX + Math.abs(y) / maxY;
    if (octagonal > 1.55) {
      const scale = 1.55 / octagonal;
      x *= scale;
      y *= scale;
    }
  }
  return { x, y };
}

function createObjectiveMarker(): void {
  if (objectiveMarker) {
    scene.remove(objectiveMarker);
    (objectiveMarker.material as THREE.Material).dispose();
    objectiveMarker.geometry.dispose();
  }
  const material = new THREE.MeshBasicMaterial({ color: 0xffe278, transparent: true, opacity: 0.9, depthWrite: false });
  objectiveMarker = new THREE.Mesh(new THREE.PlaneGeometry(0.2, 0.2), material);
  objectiveMarker.rotation.z = Math.PI * 0.25;
  objectiveMarker.renderOrder = 40;
  scene.add(objectiveMarker);
}

function createFriendlyFlagMarker(): void {
  if (friendlyFlagMarker) {
    scene.remove(friendlyFlagMarker);
    (friendlyFlagMarker.material as THREE.Material).dispose();
    friendlyFlagMarker.geometry.dispose();
  }
  const material = new THREE.MeshBasicMaterial({ color: 0x7ff5e8, transparent: true, opacity: 0.88, depthWrite: false });
  friendlyFlagMarker = new THREE.Mesh(new THREE.PlaneGeometry(0.24, 0.24), material);
  friendlyFlagMarker.rotation.z = Math.PI * 0.25;
  friendlyFlagMarker.renderOrder = 40;
  const flag = friendlyFlagPosition();
  friendlyFlagMarker.position.set(flag.x, flag.y + 0.45, 6.4);
  scene.add(friendlyFlagMarker);
}

function spawnFlagReinforcement(): void {
  const objective = objectivePosition();
  for (let i = 0; i < 4; i += 1) {
    const angle = i / 4 * Math.PI * 2 + random() * 0.35;
    const radius = 1.1 + random() * 0.7;
    units.push(createUnit(i === 3 && currentStage.id > 1 ? "lancer" : "swordsman", objective.x + Math.cos(angle) * radius, objective.y + Math.sin(angle) * radius * 0.7));
  }
  reinforcementCount += 1;
  reinforcementTimer = 11 + currentStage.id * 1.5;
  announce("敵旗より増援！", 0.65);
}

function spawnWave(waveIndex: number): void {
  waveCenter = clampToPlayable({ x: hero.x, y: hero.y });
  routRemaining = 0; waveStartedAt = elapsed;
  engagedMeleeIds.clear();
  engagedRangedIds.clear();
  const wave = currentStage.waves[waveIndex];
  const entries = Object.entries(wave) as [EnemyKind, number][];
  let enemyIndex = 0;
  for (const [kind, count] of entries) {
    for (let i = 0; i < count; i += 1) {
      const angle = (enemyIndex + waveIndex * 3) * 2.399 + random() * 0.42;
      const ring = 2.5 + (enemyIndex % 4) * 1.25 + random() * 0.5;
      let x = hero.x + Math.cos(angle) * ring;
      let y = hero.y + Math.sin(angle) * ring * 0.66;
      if (kind === "archer") { x = hero.x + (x - hero.x) * 1.35; y = hero.y + (y - hero.y) * 1.35; }
      if (kind === "monster" || kind === "mountedBoss") {
        x = hero.x + (random() > 0.5 ? 1 : -1) * (7 + random() * 5);
        y = hero.y + (random() - 0.5) * 8;
      }
      const playable = clampToPlayable({ x, y });
      x = playable.x;
      y = playable.y;
      units.push(createUnit(kind, x, y));
      enemyIndex += 1;
    }
  }
  const flagOfficer = units
    .filter((unit) => unit.team === "enemy" && unit.state !== "dead")
    .sort((a, b) => (a.kind === "mountedBoss" ? -10 : a.kind === "lancer" ? -1 : 0) - (b.kind === "mountedBoss" ? -10 : b.kind === "lancer" ? -1 : 0))[0];
  objectiveTargetId = flagOfficer?.id ?? null;
  if (flagOfficer && flagOfficer.kind !== "mountedBoss") {
    flagOfficer.hp = Math.max(flagOfficer.hp, 92 + waveIndex * 18);
    flagOfficer.maxHp = flagOfficer.hp;
    flagOfficer.launchResistance = Math.max(flagOfficer.launchResistance, 0.28);
    const shadowMaterial = flagOfficer.shadow.material as THREE.MeshBasicMaterial;
    shadowMaterial.color.setHex(0xffd65c);
    const objective = objectivePosition();
    flagOfficer.x = objective.x;
    flagOfficer.y = objective.y;
    createObjectiveMarker();
  }
  if (flagOfficer?.kind === "mountedBoss") { const pos = objectivePosition(); flagOfficer.x = pos.x; flagOfficer.y = pos.y; createObjectiveMarker(); }
  reinforcementTimer = 9 + currentStage.id;
  reinforcementCount = 0;
  waveTransition = 0;
  placeStageProps();
}

function clearStageProps(): void {
  for (const prop of stageProps) { scene.remove(prop.mesh); (prop.mesh.material as THREE.Material).dispose(); }
  stageProps.length = 0;
}

function placeStageProps(): void {
  clearStageProps();
  const place = (x: number, y: number, frame: number, supply = false): void => {
    const material = makeAtlasMaterial(textures.scenery, new THREE.Color(0xffffff), new THREE.Color(0), 0);
    setAtlasGridFrame(material, 2, 2, Math.floor(frame / 2), frame % 2);
    const mesh = new THREE.Mesh(unitGeometry, material);
    mesh.scale.setScalar(96 * PIXEL_WORLD);
    mesh.position.set(snapPixel(x), snapPixel(y + 45 * PIXEL_WORLD), 2.5 - y * 0.003);
    mesh.renderOrder = 1000 - Math.round(y * 48);
    scene.add(mesh); stageProps.push({ x, y, mesh, supply, taken: false });
  };
  const motif = currentStage.id === 1 ? 0 : currentStage.id === 3 ? 2 : 1;
  for (const side of [-1, 1]) for (const y of [-3.1, 0.5, 3.7]) place(waveCenter.x + side * (isPortraitBattle() ? 2.65 : 5.5), waveCenter.y + y, motif);
  const objective = objectivePosition(); place(objective.x + 0.7, objective.y + 0.8, 2);
  place(waveCenter.x - 1.65, waveCenter.y - 1.75, 3, true);
  place(waveCenter.x + 1.65, waveCenter.y - 2.6, 3, true);
}

function refreshEngagementAssignments(): void {
  const eligible = (unit: Unit): boolean => unit.team === "enemy" && unit.state !== "dead" && unit.state !== "launched" && unit.state !== "down";
  const raidsFriendlyFlag = (unit: Unit): boolean => friendlyFlagHp > 0 && unit.id % 11 === 0;
  const meleeCandidates = units
    .filter((unit) => eligible(unit) && unit.kind !== "archer" && !raidsFriendlyFlag(unit))
    .sort((a, b) => (a.kind === "mountedBoss" ? -10000 : distanceSq(a, hero)) - (b.kind === "mountedBoss" ? -10000 : distanceSq(b, hero)));
  const rangedCandidates = units
    .filter((unit) => eligible(unit) && unit.kind === "archer" && !raidsFriendlyFlag(unit))
    .sort((a, b) => distanceSq(a, hero) - distanceSq(b, hero));

  const meleeIds = new Set(meleeCandidates.map((unit) => unit.id));
  const rangedIds = new Set(rangedCandidates.map((unit) => unit.id));
  for (const id of engagedMeleeIds) if (!meleeIds.has(id)) engagedMeleeIds.delete(id);
  for (const id of engagedRangedIds) if (!rangedIds.has(id)) engagedRangedIds.delete(id);

  const meleeLimit = meleeEngagementLimit();
  if (engagedMeleeIds.size > meleeLimit) {
    const removable = meleeCandidates
      .filter((unit) => engagedMeleeIds.has(unit.id) && unit.aiMode !== "windup" && unit.aiMode !== "strike")
      .sort((a, b) => distanceSq(b, hero) - distanceSq(a, hero));
    while (engagedMeleeIds.size > meleeLimit && removable.length > 0) engagedMeleeIds.delete(removable.shift()!.id);
  }

  for (const unit of meleeCandidates) {
    if (engagedMeleeIds.size >= meleeLimit) break;
    engagedMeleeIds.add(unit.id);
  }
  for (const unit of rangedCandidates) {
    if (engagedRangedIds.size >= MAX_ENGAGED_RANGED) break;
    engagedRangedIds.add(unit.id);
  }
}

function damageFriendlyFlag(amount: number): void {
  if (friendlyFlagHp <= 0 || ended) return;
  friendlyFlagHp = Math.max(0, friendlyFlagHp - amount);
  friendlyFlagUnderAttack = true;
  const tier = Math.ceil(friendlyFlagHp / 25);
  if (tier < friendlyFlagWarningTier && friendlyFlagHp > 0) {
    friendlyFlagWarningTier = tier;
    announce(`翠旗危急　残り${Math.ceil(friendlyFlagHp)}%`, 0.75);
  }
  if (friendlyFlagHp <= 0) {
    announce("翠旗陥落", 1);
    finishBattle(false);
  }
}

function updateWatchingEnemy(unit: Unit, dt: number): void {
  if (routRemaining > 0) {
    moveCardinal(unit, { x: unit.x >= waveCenter.x ? 1 : -1, y: 0 }, 2.7, dt);
    unit.state = "walk"; return;
  }
  if (unit.id === objectiveTargetId) {
    const objective = objectivePosition();
    const dx = objective.x - unit.x;
    const dy = objective.y - unit.y;
    const distance = Math.hypot(dx, dy);
    const towardHero = normalize({ x: hero.x - unit.x, y: hero.y - unit.y });
    unit.facing = Math.atan2(towardHero.y, towardHero.x);
    if (distance > 0.3) {
      moveCardinal(unit, { x: dx, y: dy }, 1.55, dt);
      unit.state = "walk";
    } else unit.state = "idle";
    return;
  }
  if (unit.id % 11 === 0 && elapsed > 4) {
    const flag = friendlyFlagPosition();
    const towardFlag = normalize({ x: flag.x - unit.x, y: flag.y - unit.y });
    const flagDistance = Math.sqrt(distanceSq(unit, flag));
    unit.facing = Math.atan2(towardFlag.y, towardFlag.x);
    if (flagDistance > 1.65) {
      moveCardinal(unit, towardFlag, 1.7, dt);
      unit.state = "walk";
    } else if (unit.attackCooldown <= 0) {
      unit.state = "attack";
      unit.stateTime = 0;
      unit.attackCooldown = 1.35 + random() * 0.45;
      damageFriendlyFlag(2);
      addImpactStar(flag.x, flag.y, 0x9fffee, 0.65);
    } else if (unit.state !== "attack" || unit.stateTime > 0.28) unit.state = "idle";
    if (flagDistance < 1.8) friendlyFlagUnderAttack = true;
    return;
  }
  const allyCandidates = units
    .filter((candidate) => candidate.team === "player" && candidate.kind !== "hero" && candidate.state !== "dead")
    .sort((a, b) => a.id - b.id);
  const canSparInCurrentLayout = !isPortraitBattle() || unit.id % 3 === 0;
  const sparringTarget = unit.id % 4 === 0 && canSparInCurrentLayout && allyCandidates.length > 0 ? allyCandidates[unit.id % allyCandidates.length] : undefined;
  const sparringRange = isPortraitBattle() ? 3.5 : 5.2;
  if (sparringTarget && distanceSq(unit, sparringTarget) < sparringRange * sparringRange) {
    const toward = normalize({ x: sparringTarget.x - unit.x, y: sparringTarget.y - unit.y });
    const distance = Math.sqrt(distanceSq(unit, sparringTarget));
    unit.facing = Math.atan2(toward.y, toward.x);
    if (distance > 1.18) {
      moveCardinal(unit, toward, 1.55, dt); unit.state = "walk";
    } else if (unit.attackCooldown <= 0) {
      unit.state = "attack"; unit.stateTime = 0; unit.attackCooldown = 0.8 + random() * 0.8; sparringTarget.flash = 0.06;
    } else if (unit.state !== "attack" || unit.stateTime > 0.28) unit.state = "idle";
    return;
  }
  const isArcher = unit.kind === "archer";
  const baseRadius = isArcher ? 8.8 : unit.kind === "monster" ? 6.8 : 5.2;
  const radius = baseRadius + (unit.id % 4) * (isArcher ? 0.75 : 0.65);
  const directionSign = unit.id % 2 === 0 ? 1 : -1;
  const angle = unit.id * 2.399 + currentWaveIndex * 0.73 + elapsed * 0.075 * directionSign;
  const portrait = isPortraitBattle();
  const lane = unit.id % 3 - 1;
  const portraitReserve = portrait && unit.id % 2 !== 0;
  const portraitDistance = portraitReserve ? 4.4 + unit.id % 4 * 0.6 : 2.0 + Math.floor(unit.id % 6 / 3) * 1.05;
  const targetX = portrait ? waveCenter.x + lane * (isArcher ? 1.65 : 1.3) : waveCenter.x + Math.cos(angle) * radius;
  const targetY = portrait ? waveCenter.y + (unit.id % 4 < 2 ? 1 : -1) * portraitDistance : waveCenter.y + Math.sin(angle) * radius * 0.68;
  const dx = targetX - unit.x;
  const dy = targetY - unit.y;
  const distance = Math.hypot(dx, dy);
  const toHero = normalize({ x: hero.x - unit.x, y: hero.y - unit.y });
  unit.facing = Math.atan2(toHero.y, toHero.x);
  const cadence = (elapsed + unit.aiPhase) % 3.6;
  if (distance > 0.34) {
    const baseSpeed = portraitReserve ? 3.8 : isArcher ? 1.6 : 1.85;
    const speed = cadence < 0.42 ? baseSpeed * 1.75 : cadence > 2.9 ? baseSpeed * 0.48 : baseSpeed;
    moveCardinal(unit, { x: dx, y: dy }, speed, dt);
    unit.state = "walk";
  } else {
    unit.state = cadence < 0.52 ? "guard" : "idle";
  }
}

function applyAllyHit(attacker: Unit, target: Unit, damage: number, launch: number): void {
  if (target.state === "dead" || target.state === "launched" || target.invulnerable > 0) return;
  const direction = normalize({ x: target.x - attacker.x, y: target.y - attacker.y });
  target.hp -= damage;
  target.flash = 0.1;
  addSpark(target.x, target.y + 0.25, attacker.kind === "lancer" ? 0x8cecff : 0xb8f3ff, 3);
  // A finishing shield hit still needs a defeat/landing transition.
  // Leaving a zero-HP unit idle makes it untargetable and can block wave end.
  if (target.hp <= 0 && launch <= 0.5) launch = 3.2;
  if (launch <= 0.5) return;
  target.vx = direction.x * launch * clamp(1 - target.launchResistance, 0.35, 1);
  target.vy = direction.y * launch * 0.72 * clamp(1 - target.launchResistance, 0.35, 1);
  target.vz = 3.2 * clamp(1 - target.launchResistance * 0.3, 0.72, 1);
  target.air = 0.03;
  target.state = "launched";
  target.stateTime = 0;
  target.aiMode = "watch";
  target.aiTimer = 0;
}

function updateAlly(unit: Unit, dt: number): void {
  if (unit.state === "launched" || unit.state === "down" || unit.state === "dead") return;
  unit.attackCooldown -= dt;
  const enemyCandidates = units
    .filter((candidate) => candidate.team === "enemy" && candidate.state !== "dead" && candidate.state !== "launched")
    .sort((a, b) => a.id - b.id);
  const defender = unit.id % 5 === 0;
  const flag = friendlyFlagPosition();
  const raiders = defender ? enemyCandidates.filter((candidate) => candidate.id % 11 === 0 && distanceSq(candidate, flag) < 4.5 * 4.5) : [];
  const nearest = [...enemyCandidates].sort((a, b) => distanceSq(unit, a) - distanceSq(unit, b))[0];
  const target = raiders.length > 0 ? raiders.sort((a, b) => distanceSq(unit, a) - distanceSq(unit, b))[0] : unit.kind === "archer" ? nearest : defender ? undefined : enemyCandidates.length > 0 ? enemyCandidates[(unit.id * 3) % enemyCandidates.length] : undefined;
  const targetDistance = target ? Math.sqrt(distanceSq(unit, target)) : Infinity;
  const portrait = isPortraitBattle();

  if (unit.kind === "archer" && unit.aiMode === "windup") {
    unit.aiTimer -= dt;
    unit.state = "guard";
    if (unit.aiTimer <= 0) {
      unit.aiMode = "strike";
      unit.aiTimer = 0.24;
      unit.state = "attack";
      unit.stateTime = 0;
      if (target) {
        const toward = normalize({ x: target.x - unit.x, y: target.y - unit.y });
        createProjectile(unit.x + toward.x * 0.72, unit.y + toward.y * 0.72, toward.x * 9.4, toward.y * 9.4, false, ALLY_ARROW_SKILL, true);
      }
    }
    return;
  }
  if (unit.kind === "archer" && unit.aiMode === "strike") {
    unit.aiTimer -= dt;
    if (unit.aiTimer <= 0) { unit.aiMode = "watch"; unit.state = "idle"; }
    return;
  }

  const awareness = unit.kind === "archer" ? (portrait ? 8.2 : 12) : portrait ? 2.8 : 5.2;
  if (!target || targetDistance > awareness) {
    const angle = unit.id * 2.399;
    const formation = defender
      ? { x: flag.x + (unit.id % 3 - 1) * 0.7, y: flag.y + 0.7 + unit.id % 2 * 0.5 }
      : portrait
      ? { x: hero.x + (unit.id % 3 - 1) * (unit.kind === "archer" ? 1.75 : 1.45), y: hero.y + (unit.id % 2 === 0 ? 1 : -1) * (unit.kind === "archer" ? 3.45 : 2.25 + unit.id % 3 * 0.48) }
      : { x: hero.x + Math.cos(angle) * (unit.kind === "archer" ? 4.4 : 2.1 + unit.id % 3), y: hero.y + Math.sin(angle) * (unit.kind === "archer" ? 3.1 : 1.4 + unit.id % 3 * 0.45) };
    const towardFormation = normalize({ x: formation.x - unit.x, y: formation.y - unit.y });
    const formationDistance = Math.hypot(formation.x - unit.x, formation.y - unit.y);
    if (formationDistance > 0.45) {
      moveCardinal(unit, towardFormation, unit.kind === "archer" ? 2.25 : 2.05, dt);
      unit.state = "walk";
    } else unit.state = unit.kind === "swordsman" && (elapsed + unit.aiPhase) % 2.8 < 0.34 ? "guard" : "idle";
    return;
  }

  const toward = normalize({ x: target.x - unit.x, y: target.y - unit.y });
  const distance = targetDistance;
  unit.facing = Math.atan2(toward.y, toward.x);
  if (unit.kind === "archer") {
    if (distance < 3.4) {
      moveCardinal(unit, { x: -toward.x, y: -toward.y }, 3.05, dt);
      unit.state = "walk";
    } else if (distance > 6.8) {
      moveCardinal(unit, toward, 2.2, dt);
      unit.state = "walk";
    } else if (unit.attackCooldown <= 0) {
      unit.aiMode = "windup";
      unit.aiTimer = 0.38;
      unit.attackCooldown = 2.25 + random() * 0.85;
      unit.state = "guard";
      unit.stateTime = 0;
    } else unit.state = "idle";
    return;
  }

  const desiredDistance = unit.kind === "lancer" ? 2.05 : portrait ? 1.42 : 1.08;
  if (distance > desiredDistance) {
    moveCardinal(unit, toward, unit.kind === "lancer" ? 2.05 : 1.72, dt);
    unit.state = "walk";
  } else if (unit.attackCooldown <= 0) {
    unit.state = "attack"; unit.stateTime = 0; unit.attackCooldown = unit.kind === "lancer" ? 1.55 + random() * 0.75 : 1.25 + random() * 0.85;
    applyAllyHit(unit, target, unit.kind === "lancer" ? 6 : 3, unit.kind === "lancer" ? 3.2 : 0);
  } else if (unit.state !== "attack" || unit.stateTime > 0.28) unit.state = unit.kind === "swordsman" && (elapsed + unit.aiPhase) % 2.8 < 0.32 ? "guard" : "idle";
}

function spawnStage(stageNumber: number): void {
  clearSceneObjects();
  currentStage = STAGES[clamp(stageNumber - 1, 0, STAGES.length - 1)];
  seed = Number(new URLSearchParams(location.search).get("seed") ?? 4242) + currentStage.id * 1009;
  random = mulberry32(seed);
  nextId = 1;
  elapsed = 0; timeLeft = currentStage.timeLimit; ko = 0; combo = 0; comboTimer = 0; spirit = 20; ended = false; currentWaveIndex = 0; waveTransition = 0;
  pointerMoveTarget = null; pointerAttackTargetId = null; primaryChain = 0; primaryChainTimer = 0; objectiveTargetId = null;
  rhythm = new CombatRhythm(); releasedCharge = 0; dodgeCount = 0; perfectDodgeCount = 0; counterCount = 0; paused = false; simulationAccumulator = 0;
  waveCenter = { x: 0, y: 0 }; routRemaining = 0; escapedEnemies = 0; reflectedArrows = 0;
  suppliesCollected = 0;
  friendlyFlagHp = 100; friendlyFlagUnderAttack = false; friendlyFlagWarningTier = 4;
  freeze = 0; shake = 0; autoplayClock = 0; autoplayStep = -1; activeLaunches = 0; lastAttackLaunched = 0;
  cooldowns.forEach((_value, key) => cooldowns.set(key, 0));
  drawGround(currentStage);
  hero = createUnit("hero", 0, 0);
  units.push(hero);
  createFriendlyFlagMarker();
  const allyCount = 9 + currentStage.id;
  for (let i = 0; i < allyCount; i += 1) {
    const angle = i * 2.399;
    const radius = 1.8 + (i % 4) * 0.72;
    const allyKind: Unit["kind"] = i % 5 === 0 ? "archer" : i % 3 === 0 ? "lancer" : "swordsman";
    const ally = createUnit(allyKind, Math.cos(angle) * radius, Math.sin(angle) * radius * 0.72, "player");
    ally.hp = 120; ally.maxHp = 120; ally.launchResistance = 0.42;
    units.push(ally);
  }
  spawnWave(0);
  cameraPosition.set(0, 0);
  cameraTarget.set(0, 0);
  metrics.seed = seed; metrics.stage = currentStage.id; metrics.peakVisibleEnemies = 0; metrics.maxLaunchedAtOnce = 0; metrics.ko = 0;
  metrics.lastSkill = "none"; metrics.skillTriggers = { weak: 0, mid: 0, long: 0, area: 0, engage: 0, disengage: 0 }; metrics.touchActions = 0;
  metrics.spirit = spirit; metrics.musouTriggers = 0; metrics.lastAttackWasMusou = false;
  metrics.enemyWindups = 0; metrics.enemyRetreating = 0; metrics.enemyAttackStarts = 0; metrics.enemyRetreatStarts = 0; metrics.peakEnemyWindups = 0; metrics.engagementLimit = MAX_ENGAGED_MELEE;
  metrics.pointerEvents = 0; metrics.pointerTargetX = 0; metrics.pointerTargetY = 0; metrics.pointerTargetKind = "none";
  metrics.reinforcementCount = 0; metrics.objectiveAlive = false; metrics.objectiveDistance = 0;
  metrics.friendlyFlagHp = 100; metrics.friendlyFlagUnderAttack = false; metrics.battleEnded = false;
  running = true;
  updateHud();
  announce(`第1波　旗頭を討て`, 1.35);
  document.querySelector("#title-screen")?.classList.remove("visible");
  document.querySelector("#result-screen")?.classList.remove("visible");
}

function spawnHorsePickup(): void {
  if (horseSpawned || horsePickup || mountedTime > 0) return;
  horseSpawned = true;
  const angle = hero.facing + Math.PI * 0.55;
  const pickup = clampToPlayable({ x: hero.x + Math.cos(angle) * 4.2, y: hero.y + Math.sin(angle) * 3.2 });
  const x = pickup.x;
  const y = pickup.y;
  const mesh = createHorseSprite();
  const shadow = new THREE.Mesh(shadowGeometry, new THREE.MeshBasicMaterial({ color: 0x1b2618, transparent: true, opacity: 0.26, depthWrite: false }));
  shadow.scale.set(1.45, 0.38, 1);
  scene.add(shadow);
  horsePickup = { x, y, mesh, shadow };
  addSlash(x, y, hero.facing, 1.6, 0xeaff78);
  announce("軍馬来援", 0.6);
}

function beginMountedMode(): void {
  if (!horsePickup) return;
  scene.remove(horsePickup.mesh, horsePickup.shadow);
  (horsePickup.mesh.material as THREE.Material).dispose();
  (horsePickup.shadow.material as THREE.Material).dispose();
  horsePickup = null;
  horseMount = createHorseSprite();
  mountedTime = 12;
  hero.invulnerable = 12;
  spirit = 100;
  shake = Math.max(shake, 0.24);
  addSlash(hero.x, hero.y, hero.facing, 2.2, 0xf3ff8f);
  announce("軍馬、来たる！", 0.6);
}

function endMountedMode(): void {
  if (horseMount) {
    scene.remove(horseMount);
    (horseMount.material as THREE.Material).dispose();
    horseMount = null;
  }
  mountedTime = 0;
  announce("軍馬離脱", 0.45);
}

function updateHorse(dt: number): void {
  if (!horseSpawned && (perfectDodgeCount >= 2 && ko >= 18 || elapsed >= 65 && ko >= 30)) spawnHorsePickup();
  if (horsePickup) {
    const pickupHeight = HORSE_CELL.height * PIXEL_WORLD;
    const pickupAnchor = pickupHeight * 0.5 - horseBottomPadding(0, 0) * pickupHeight;
    horsePickup.mesh.position.set(snapPixel(horsePickup.x), snapPixel(horsePickup.y + pickupAnchor), 3.4 - horsePickup.y * 0.003);
    horsePickup.shadow.position.set(horsePickup.x, horsePickup.y, 0.18 - horsePickup.y * 0.001);
    const material = horsePickup.mesh.material as THREE.ShaderMaterial;
    setHorseFrame(material, 0, Math.floor(elapsed * 2.5) % 2);
    material.uniforms.opacity.value = 0.88 + Math.sin(elapsed * 5) * 0.12;
    if (distanceSq(hero, horsePickup) < 2.1 * 2.1) beginMountedMode();
  }
  if (!horseMount || mountedTime <= 0) return;
  mountedTime -= dt;
  trampleCooldown = Math.max(0, trampleCooldown - dt);
  hero.invulnerable = Math.max(hero.invulnerable, 0.22);
  const moving = hero.state === "walk";
  const material = horseMount.material as THREE.ShaderMaterial;
  const row = horseRowForFacing(hero.facing);
  const column = moving ? 2 + (Math.floor(elapsed * 10) % 4) : Math.floor(elapsed * 2.5) % 2;
  setHorseFrame(material, row, column);
  const mountHeight = HORSE_CELL.height * PIXEL_WORLD;
  const mountAnchor = mountHeight * 0.5 - horseBottomPadding(row, column) * mountHeight;
  horseMount.position.set(snapPixel(hero.x), snapPixel(hero.y + mountAnchor), 2.3 - hero.y * 0.003);
  horseMount.renderOrder = hero.mesh.renderOrder - 1;
  if (trampleCooldown <= 0) {
    const mountedSkill = SKILLS.find((skill) => skill.id === "engage")!;
    let trampled = 0;
    for (const target of units) {
      if (target.team !== "enemy" || target.state === "dead" || target.state === "launched" || distanceSq(hero, target) > Math.pow(2 + target.radius, 2)) continue;
      const direction = normalize({ x: target.x - hero.x, y: target.y - hero.y });
      if (applyHit(target, 72, direction, 20, 12, mountedSkill, 1.8)) trampled += 1;
    }
    if (trampled > 0) {
      trampleCooldown = 0.16;
      shake = Math.max(shake, 0.12);
      audio.hit(1.2);
    }
  }
  if (mountedTime <= 0) endMountedMode();
}

function updateClashPanel(): void {
  const panel = document.querySelector<HTMLElement>("#clash-panel")!;
  panel.classList.toggle("active", Boolean(clashState));
  if (!clashState) return;
  const ratio = clamp(clashState.meter / clashState.required, 0, 1);
  panel.querySelector<HTMLElement>("i")!.style.width = `${ratio * 100}%`;
  panel.querySelector<HTMLElement>("span")!.textContent = `攻撃ボタンを連打　${clashState.meter}/${clashState.required}`;
}

function startClash(boss: Unit): void {
  if (clashState) return;
  announceTimer = 0;
  document.querySelector("#announcement")?.classList.remove("show");
  clashState = { bossId: boss.id, time: 2.2, meter: 0, required: 7 };
  boss.state = "stunned"; boss.stateTime = 0; boss.vx = 0; boss.vy = 0; boss.chargePower = 0;
  hero.state = "stunned"; hero.stateTime = 0; hero.vx = 0; hero.vy = 0; hero.invulnerable = 2.4;
  freeze = 0.08;
  shake = Math.max(shake, 0.18);
  updateClashPanel();
}

function resolveClash(success: boolean): void {
  if (!clashState) return;
  const boss = units.find((unit) => unit.id === clashState!.bossId);
  clashState = null;
  updateClashPanel();
  hero.state = "idle";
  if (boss && boss.state !== "dead") {
    boss.state = "idle"; boss.invulnerable = 0; boss.attackCooldown = 2.8;
    if (success) {
      boss.aiMode = "retreat";
      const engage = SKILLS.find((skill) => skill.id === "engage")!;
      const direction = normalize({ x: boss.x - hero.x, y: boss.y - hero.y });
      applyHit(boss, 74, direction, 20, 12, engage, 2.8);
    }
  }
  if (success) {
    addSlash(hero.x, hero.y, hero.facing, 2.8, 0xffe879);
    shake = Math.max(shake, 0.4);
    announce("競り勝ち　騎将崩し！", 1.1);
  } else {
    hero.invulnerable = 0;
    damageHero(42, boss ? normalize({ x: hero.x - boss.x, y: hero.y - boss.y }) : { x: 0, y: -1 });
    announce("競り負け", 0.9);
  }
}

function updateClash(dt: number): void {
  if (!clashState) return;
  clashState.time -= dt;
  hero.invulnerable = Math.max(hero.invulnerable, 0.2);
  if (clashState.time <= 0) resolveClash(false);
}

function movementDirection(): Vec2 {
  let x = (keyState.has("KeyD") || keyState.has("ArrowRight") ? 1 : 0) - (keyState.has("KeyA") || keyState.has("ArrowLeft") ? 1 : 0) + stickVector.x;
  let y = (keyState.has("KeyW") || keyState.has("ArrowUp") ? 1 : 0) - (keyState.has("KeyS") || keyState.has("ArrowDown") ? 1 : 0) - stickVector.y;
  if (Math.hypot(x, y) <= 0.12 && pointerMoveTarget) {
    const targetUnit = pointerAttackTargetId === null ? undefined : units.find((unit) => unit.id === pointerAttackTargetId && unit.state !== "dead");
    if (targetUnit) pointerMoveTarget = { x: targetUnit.x, y: targetUnit.y };
    x = pointerMoveTarget.x - hero.x;
    y = pointerMoveTarget.y - hero.y;
    if (Math.hypot(x, y) < (targetUnit ? 0.82 : 0.22)) {
      pointerMoveTarget = null;
      if (pointerMarker) {
        scene.remove(pointerMarker);
        (pointerMarker.material as THREE.Material).dispose();
        pointerMarker.geometry.dispose();
        pointerMarker = null;
      }
      if (targetUnit) {
        hero.facing = Math.atan2(targetUnit.y - hero.y, targetUnit.x - hero.x);
        pointerAttackTargetId = null;
        performPrimaryAttack();
      }
      return { x: 0, y: 0 };
    }
  }
  if (Math.hypot(x, y) <= 0.12) return { x: 0, y: 0 };
  // Classic four-direction movement: analog input snaps to its dominant
  // axis, preserving the sprite language and avoiding accidental diagonals.
  return Math.abs(x) >= Math.abs(y) ? { x: Math.sign(x), y: 0 } : { x: 0, y: Math.sign(y) };
}

function heroFacingVector(): Vec2 { return { x: Math.cos(hero.facing), y: Math.sin(hero.facing) }; }

function performPrimaryAttack(): void {
  if (clashState) { attack("weak"); return; }
  if (!rhythm.canSlash || cooldowns.get("weak")! > 0 || hero.state === "stunned") { rhythm.requestSlash(); return; }
  rhythm.buffered = 0;
  primaryChain = primaryChainTimer > 0 ? (primaryChain + 1) % 3 : 0;
  primaryChainTimer = 1.05;
  attack(primaryChain === 2 ? "mid" : "weak");
}

function inputFacing(): Vec2 | null {
  const x = (keyState.has("KeyD") || keyState.has("ArrowRight") ? 1 : 0) - (keyState.has("KeyA") || keyState.has("ArrowLeft") ? 1 : 0) + stickVector.x;
  const y = (keyState.has("KeyW") || keyState.has("ArrowUp") ? 1 : 0) - (keyState.has("KeyS") || keyState.has("ArrowDown") ? 1 : 0) - stickVector.y;
  return Math.hypot(x, y) > 0.15 ? cardinalDirection({ x, y }) : null;
}

function evade(backward = false): void {
  const liveDirection = inputFacing();
  const facing = heroFacingVector();
  const direction = liveDirection ?? { x: facing.x * (backward ? -1 : 1), y: facing.y * (backward ? -1 : 1) };
  if (!rhythm.beginDodge(direction)) return;
  const threats = units.filter((unit) => unit.team === "enemy" && unit.hp > 0 && distanceSq(unit, hero) < 3.5 ** 2 && (unit.aiMode === "windup" && unit.aiTimer < 0.3 || unit.aiMode === "strike"));
  const nearArrow = projectiles.some((p) => p.hostile && distanceSq(p, hero) < 1.6 ** 2);
  if (threats.length || nearArrow) {
    rhythm.counter = 1.6;
    perfectDodgeCount += 1;
    spirit = clamp(spirit + 18, 0, 100);
    for (const threat of threats) { threat.aiMode = "retreat"; threat.aiTimer = 0.85; threat.vx = 0; threat.vy = 0; threat.state = "guard"; }
    announce("見切り！ 次の斬撃で反撃", 0.7);
    audio.slash(0.65);
  }
  for (const enemy of threats) if (enemy.kind === "mountedBoss" && enemy.chargePower > 0) startClash(enemy);
  hero.state = "idle"; hero.stateTime = 0; hero.air = 0; hero.vx = 0; hero.vy = 0; hero.vz = 0;
  hero.mesh.rotation.z = 0;
  hero.invulnerable = Math.max(hero.invulnerable, 0.34);
  hero.facing = Math.atan2(direction.y, direction.x);
  pointerMoveTarget = null; pointerAttackTargetId = null;
  freeze = 0;
  dodgeCount += 1;
  audio.dash();
}

function beginCharge(): void {
  if (!running || ended || paused || rhythm.dodge > 0 || cooldowns.get("area")! > 0) return;
  rhythm.charging = true; rhythm.charge = 0;
}

function releaseCharge(): void {
  if (!rhythm.charging) return;
  releasedCharge = rhythm.charge;
  rhythm.charging = false; rhythm.charge = 0;
  attack("area");
  releasedCharge = 0;
}

function applyHit(target: Unit, damage: number, direction: Vec2, launch: number, vertical: number, skill: SkillSpec, slamPower = 0, rewardsPlayer = true): boolean {
  if (target.invulnerable > 0 || target.state === "dead" || target.hp <= 0) return false;
  const armored = target.id === objectiveTargetId || target.kind === "mountedBoss";
  const breaksGuard = skill.id === "mid" || skill.id === "long" || damage >= 80;
  if (armored && !breaksGuard && target.aiMode !== "retreat" && target.state !== "down") {
    target.hp = Math.max(1, target.hp - 4); target.flash = 0.05; target.invulnerable = 0.12;
    addImpactStar(target.x, target.y, 0xa9d9ee, 0.6);
    return false;
  }
  const dir = fanDirection(normalize(direction), target.id);
  target.hp -= damage;
  const survivingElite = target.hp > 0 && (armored || target.kind === "monster");
  const force = survivingElite ? Math.min(3.2, launch) : launch * clamp(1 - target.launchResistance, 0.32, 1);
  target.vx = dir.x * force;
  target.vy = dir.y * force * 0.76;
  target.vz = survivingElite ? 3.2 : vertical * clamp(1 - target.launchResistance * 0.35, 0.7, 1);
  target.air = Math.max(target.air, 0.04);
  target.state = "launched";
  target.aiMode = "watch";
  target.aiTimer = 0;
  target.slamPower = Math.max(target.slamPower, slamPower);
  target.stateTime = 0;
  target.invulnerable = 0.12;
  target.flash = 0.16;
  target.facing = Math.atan2(-dir.y, -dir.x);
  if (rewardsPlayer) {
    combo += 1;
    comboTimer = 2.2;
    if (skill !== MUSOU_SKILL) spirit = clamp(spirit + 0.35 + damage * 0.009, 0, 100);
  }
  const impactColor = skill.id === "long" ? 0x78e9ff : skill.id === "area" ? 0xc59aff : 0xfff2bb;
  addSpark(target.x, target.y + 0.25, skill.color, damage > 35 ? 7 : 4);
  if (damage > 35) addImpactStar(target.x, target.y + 0.2, impactColor, clamp(damage / 45, 0.8, 1.8));
  return true;
}

function queueDelayedHit(target: Unit, damage: number, direction: Vec2, launch: number, vertical: number, skill: SkillSpec, delay: number, slamPower: number): boolean {
  if (target.state === "dead" || target.invulnerable > 0 || target.hp <= 0) return false;
  target.state = "stunned";
  target.aiMode = "watch";
  target.aiTimer = 0;
  target.stateTime = 0;
  target.vx = 0; target.vy = 0; target.vz = 0;
  target.invulnerable = delay + 0.04;
  target.flash = 0.08;
  pendingHits.push({ targetId: target.id, delay, damage, direction: normalize(direction), launch, vertical, skill, slamPower });
  return true;
}

function updatePendingHits(dt: number): void {
  for (let i = pendingHits.length - 1; i >= 0; i -= 1) {
    const pending = pendingHits[i];
    pending.delay -= dt;
    if (pending.delay > 0) continue;
    const target = units.find((unit) => unit.id === pending.targetId);
    if (target && target.state !== "dead") {
      target.invulnerable = 0;
      target.state = "idle";
      applyHit(target, pending.damage, pending.direction, pending.launch, pending.vertical, pending.skill, pending.slamPower);
      const impactColor = pending.skill.id === "long" ? 0x78e9ff : pending.skill.id === "area" ? 0xc59aff : 0xfff2bb;
      addImpactStar(target.x, target.y, impactColor, target.kind === "mountedBoss" ? 1.8 : 1.05);
    }
    pendingHits.splice(i, 1);
  }
}

function attack(skillId: SkillId): void {
  if (!running || ended || paused) return;
  if (clashState) {
    clashState.meter += 1;
    haptic(12);
    addSpark(hero.x, hero.y + 0.8, 0xffdd78, 4);
    audio.hit(0.75);
    updateClashPanel();
    if (clashState.meter >= clashState.required) resolveClash(true);
    return;
  }
  if (skillId === "engage" || skillId === "disengage") {
    const before = dodgeCount;
    evade(skillId === "disengage");
    if (dodgeCount > before) { metrics.lastSkill = skillId; metrics.skillTriggers[skillId] += 1; }
    return;
  }
  if (cooldowns.get(skillId)! > 0 || rhythm.dodge > 0.045 || rhythm.hurt > 0 || hero.state === "launched" || hero.state === "down" || hero.state === "stunned") return;
  audio.unlock();
  haptic(skillId === "area" ? 18 : 7);
  const musou = skillId === "area" && spirit >= MUSOU_THRESHOLD;
  let skill = musou ? MUSOU_SKILL : SKILLS.find((item) => item.id === skillId)!;
  const counter = rhythm.counter > 0 && (skillId === "weak" || skillId === "mid");
  if (counter) { skill = { ...skill, damage: 80, range: 3.25, launch: 23, vertical: 10 }; rhythm.counter = 0; counterCount += 1; hero.hp = Math.min(hero.maxHp, hero.hp + runPerks.guard * 20); announce("返し一閃", 0.5); }
  const charged = skillId === "area" && !musou && releasedCharge >= Math.max(0.32, 0.5 - runPerks.power * 0.08);
  if (charged) skill = { ...skill, range: 2.35, damage: 92 + runPerks.power * 16, launch: 29, vertical: 12 };
  const liveFacing = inputFacing();
  if (liveFacing) hero.facing = Math.atan2(liveFacing.y, liveFacing.x);
  rhythm.recovery = skillId === "weak" ? Math.max(0.14, 0.2 - runPerks.flow * 0.02) : skillId === "mid" ? 0.26 : 0.3;
  metrics.lastSkill = skillId;
  metrics.lastAttackWasMusou = musou;
  metrics.skillTriggers[skillId] += 1;
  if (musou) {
    spirit = 0;
    metrics.musouTriggers += 1;
  }
  cooldowns.set(skillId, skill.cooldown * (skillId === "weak" ? Math.max(0.7, 1 - runPerks.flow * 0.1) : 1));
  hero.state = "attack";
  hero.stateTime = 0;
  hero.invulnerable = Math.max(hero.invulnerable, musou ? 1.65 : 0.08);
  const facing = heroFacingVector();
  for (const prop of stageProps) {
    if (!prop.supply || prop.taken || skillId === "long") continue;
    const hit = skillId === "area" ? distanceSq(hero, prop) < skill.range ** 2 : inArc(hero, facing, prop, 2.35, -0.15);
    if (!hit) continue;
    prop.taken = true; prop.mesh.visible = false; suppliesCollected += 1;
    hero.hp = Math.min(hero.maxHp, hero.hp + 12); spirit = clamp(spirit + 4, 0, 100);
    addImpactStar(prop.x, prop.y, 0xe3c58c, 0.7); addSpark(prop.x, prop.y, 0xb39664, 4);
  }
  if (skillId === "weak" || skillId === "mid") for (const arrow of projectiles) {
    if (!arrow.hostile || !inArc(hero, facing, arrow, 2.0, -0.15)) continue;
    const speed = Math.max(10, Math.hypot(arrow.vx, arrow.vy));
    arrow.hostile = false; arrow.vx = facing.x * speed; arrow.vy = facing.y * speed;
    arrow.damage = 45; arrow.launch = 17; arrow.vertical = 7; arrow.rewardsPlayer = true;
    (arrow.mesh.material as THREE.MeshBasicMaterial).color.setHex(0xa9ecb8);
    reflectedArrows += 1; spirit = clamp(spirit + 8, 0, 100); addImpactStar(arrow.x, arrow.y, 0xa9ecb8, 0.8);
  }
  let hits = 0;
  let hitstop = 0;

  if (skillId === "long") {
    const reach = isPortraitBattle() ? Math.abs(facing.x) > 0.5 ? 0.65 : facing.y > 0 ? 2.2 : 1 : 3.6;
    const center = clampToPlayable({
      x: clamp(hero.x + facing.x * reach, camera.position.x - camera.right + 1.35, camera.position.x + camera.right - 1.35),
      y: clamp(hero.y + facing.y * reach, camera.position.y - camera.top + 1.35, camera.position.y + camera.top - 2.0),
    });
    units.forEach((target) => {
      if (target.team !== "enemy" || target.state === "dead") return;
      const delta = { x: target.x - center.x, y: target.y - center.y };
      const distance = Math.hypot(delta.x, delta.y);
      if (distance > skill.range + target.radius) return;
      const direction = distance > 0.08 ? delta : facing;
      if (queueDelayedHit(target, skill.damage, direction, skill.launch, skill.vertical, skill, 0.24 + distance * 0.018, 2.35)) hits += 1;
    });
    addMagicBurst(center.x, center.y);
    addSpark(center.x, center.y + 0.15, skill.color, 8);
    announce("翠雷術", 0.48);
    shake = Math.max(shake, 0.24); flashOverlay = 0.1; hitstop = hits > 0 ? 0.052 : 0.025;
    zoomKick = Math.max(zoomKick, 0.016);
    audio.magic();
    haptic([9, 22, 18]);
  } else {
    const area = skillId === "area";
    units.forEach((target) => {
      if (target.team !== "enemy" || target.state === "dead") return;
      const delta = { x: target.x - hero.x, y: target.y - hero.y };
      const hit = area ? distanceSq(hero, target) <= Math.pow(skill.range + target.radius, 2) : inArc(hero, facing, target, skill.range + target.radius, 0.18);
      if (!hit) return;
      if (area) {
        const delay = musou ? 0.16 + Math.sqrt(distanceSq(hero, target)) * 0.018 : charged ? 0.24 : 0.12;
        if (queueDelayedHit(target, skill.damage, delta, skill.launch, skill.vertical, skill, delay, musou ? 3.8 : 1.8)) hits += 1;
      } else if (applyHit(target, skill.damage, facing, skill.launch, skill.vertical, skill, skillId === "mid" ? 2.2 : 1.45)) {
        hits += 1;
      }
    });
    if (area) {
      if (musou) {
        const sweepRadius = isPortraitBattle() ? 3.15 : 3.75;
        addSlash(hero.x, hero.y, hero.facing, sweepRadius, 0xffffff, 0.92, 0, 0.22);
        addSlash(hero.x, hero.y, hero.facing + 2.1, sweepRadius * 1.08, 0xbfff72, 0.72, 0.055, 0.21);
        addSlash(hero.x, hero.y, hero.facing - 2.1, sweepRadius * 1.15, 0xffe988, 0.64, 0.11, 0.2);
        addImpactStar(hero.x, hero.y + 0.15, 0xf7ffac, 2.5);
        addSpark(hero.x, hero.y + 0.25, 0xeaff91, 12);
        shake = Math.max(shake, 0.42); flashOverlay = 0.18; hitstop = hits > 0 ? 0.095 : 0.045;
        zoomKick = Math.max(zoomKick, 0.038);
        audio.musou();
        haptic([22, 24, 38]);
      } else {
        const sweepRadius = charged ? 3.4 : 2.4;
        addSlash(hero.x, hero.y, hero.facing, sweepRadius, skill.color, 0.62);
        shake = Math.max(shake, 0.2); flashOverlay = 0.08; hitstop = hits > 0 ? 0.065 : 0.02;
        zoomKick = Math.max(zoomKick, 0.018);
        audio.slash(1.6);
      }
    } else {
      addSlash(hero.x + facing.x * 0.7, hero.y + facing.y * 0.7, hero.facing, skillId === "mid" ? 2.65 : 1.85, skill.color);
      shake = Math.max(shake, hits > 0 ? 0.13 : 0.05);
      hitstop = hits > 0 ? (skillId === "mid" || counter ? 0.03 : 0.016) : 0;
      audio.slash(skillId === "mid" ? 1.25 : 0.85);
    }
  }
  if (hits > 0) audio.hit(clamp(hits / 5 + skill.damage / 45, 0.8, 2));
  if (musou) announce(`翠天無双　${hits}人斬り！`, 0.82, "musou");
  else if (hits >= 15) announce(`${hits}人斬り！`, 0.55);
  lastAttackLaunched = hits;
  metrics.lastAttackLaunched = hits;
  freeze = Math.max(freeze, hitstop);
  if (musou) updateHud();
}

function nearestEnemyDirection(): Vec2 | null {
  let nearest: Unit | undefined;
  let nearestDistance = Infinity;
  for (const target of units) {
    if (target.team !== "enemy" || target.state === "dead" || target.hp <= 0 || target.state === "launched") continue;
    const d = distanceSq(hero, target);
    if (d < nearestDistance) { nearestDistance = d; nearest = target; }
  }
  return nearest ? normalize({ x: nearest.x - hero.x, y: nearest.y - hero.y }) : null;
}

function createProjectile(x: number, y: number, vx: number, vy: number, hostile: boolean, skill?: SkillSpec, arrowVisual = hostile): void {
  const color = hostile ? 0xff754e : arrowVisual ? 0x78e9ff : skill?.color ?? 0xa8ff7a;
  const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(arrowVisual ? new THREE.PlaneGeometry(0.72, 0.07) : new THREE.RingGeometry(0.34, 0.48, 36, 1, -1.1, 2.2), material);
  mesh.rotation.z = Math.atan2(vy, vx);
  mesh.position.set(x, y + 0.55, 5);
  scene.add(mesh);
  projectiles.push({
    x, y, vx, vy, life: arrowVisual ? 3.2 : 1.25, radius: arrowVisual ? 0.3 : 0.72,
    damage: hostile ? 8 : skill!.damage, hostile,
    launch: hostile ? 3.8 : skill!.launch,
    vertical: hostile ? 2.8 : skill!.vertical,
    rewardsPlayer: !hostile && !arrowVisual,
    mesh,
  });
  if (arrowVisual) audio.arrow();
}

function updateHero(dt: number): void {
  hero.invulnerable = Math.max(0, hero.invulnerable - dt);
  if (rhythm.dodge > 0) {
    hero.x += rhythm.dodgeDirection.x * 16 * dt;
    hero.y += rhythm.dodgeDirection.y * 16 * dt;
    const point = clampToPlayable(hero); hero.x = point.x; hero.y = point.y;
    hero.state = "walk";
    return;
  }
  if (hero.state === "launched" || hero.state === "down") return;
  const direction = movementDirection();
  const moving = Math.hypot(direction.x, direction.y) > 0;
  const speed = rhythm.hurt > 0 ? 3.6 : rhythm.charging ? 3.8 : mountedTime > 0 ? 9 : hero.state === "attack" ? 5.4 : 6.3;
  if (moving) {
    hero.x += direction.x * speed * dt;
    hero.y += direction.y * speed * dt;
    hero.facing = Math.atan2(direction.y, direction.x);
    if (hero.state !== "attack") hero.state = "walk";
  } else if (hero.state !== "attack") hero.state = rhythm.charging ? "guard" : "idle";
  const playable = clampToPlayable(hero);
  hero.x = playable.x;
  hero.y = playable.y;
  if (hero.state === "attack" && hero.stateTime > 0.26) hero.state = "idle";
}

function updateMountedBoss(unit: Unit, dt: number): void {
  const toHero = { x: hero.x - unit.x, y: hero.y - unit.y };
  const distance = Math.hypot(toHero.x, toHero.y);
  const direction = normalize(toHero);
  unit.facing = Math.atan2(direction.y, direction.x);
  if (unit.aiMode === "windup") {
    unit.aiTimer -= dt; unit.state = "guard";
    if (unit.aiTimer <= 0) { unit.state = "attack"; unit.stateTime = 0; unit.aiMode = "strike"; }
    return;
  }
  if (unit.state === "attack") {
    if (unit.stateTime > 1.05) { unit.state = "idle"; unit.chargePower = 0; unit.vx = 0; unit.vy = 0; }
    return;
  }
  if (unit.attackCooldown <= 0 && distance < 8 && distance > 1.3) {
    addEnemyTelegraph(unit, direction, 0.75);
    unit.vx = direction.x * 13.5;
    unit.vy = direction.y * 10.5;
    unit.chargePower = 1;
    unit.state = "guard";
    unit.aiMode = "windup";
    unit.aiTimer = 0.75;
    unit.stateTime = 0;
    unit.attackCooldown = 4.4;
    announce("騎将突撃 — 避で見切れ！", 1);
    return;
  }
  if (distance > 4.2) {
    unit.x += direction.x * 2.7 * dt;
    unit.y += direction.y * 2.15 * dt;
    unit.state = "walk";
  } else {
    unit.state = "idle";
  }
}

function beginEnemyWindup(unit: Unit, direction: Vec2): void {
  const intent = normalize(direction);
  unit.aiMode = "windup";
  unit.state = "guard";
  unit.stateTime = 0;
  metrics.enemyAttackStarts += 1;
  if (unit.kind === "archer") {
    unit.aiTimer = 0.65;
    unit.vx = intent.x; unit.vy = intent.y;
    unit.rangedCooldown = 2.35 + random() * 1.15;
    addEnemyTelegraph(unit, intent, unit.aiTimer);
    return;
  }
  const speed = unit.kind === "monster" ? 8.2 : unit.kind === "lancer" ? 10.8 : 6.4;
  unit.aiTimer = unit.kind === "monster" ? 0.8 : unit.kind === "lancer" ? 0.62 : 0.5;
  unit.vx = intent.x * speed;
  unit.vy = intent.y * speed * 0.78;
  unit.attackCooldown = unit.kind === "monster" ? 3.4 + random() : unit.kind === "lancer" ? 2.45 + random() : 1.55 + random() * 0.75;
  addEnemyTelegraph(unit, intent, unit.aiTimer);
}

function updateEnemy(unit: Unit, dt: number): void {
  if (unit.state === "stunned" || unit.state === "launched" || unit.state === "down" || unit.state === "dead") return;
  if (routRemaining > 0) { unit.aiMode = "watch"; unit.vx = 0; unit.vy = 0; updateWatchingEnemy(unit, dt); return; }
  unit.attackCooldown -= dt;
  unit.rangedCooldown -= dt;
  if (unit.kind === "mountedBoss") {
    updateMountedBoss(unit, dt);
    return;
  }
  if (unit.aiMode === "windup") {
    unit.aiTimer -= dt;
    unit.state = "guard";
    if (unit.aiTimer <= 0) {
      const intent = normalize({ x: unit.vx, y: unit.vy });
      unit.aiMode = "strike";
      unit.state = "attack";
      unit.stateTime = 0;
      if (unit.kind === "archer") {
        createProjectile(unit.x + intent.x * 0.7, unit.y + intent.y * 0.7, intent.x * 8.8, intent.y * 8.8, true);
        unit.vx = 0; unit.vy = 0;
      } else {
        addSpark(unit.x + intent.x * 0.5, unit.y + intent.y * 0.5, unit.kind === "monster" ? 0xff8a55 : 0xffc56b, 2);
      }
    }
    return;
  }
  if (unit.aiMode === "strike") {
    const strikeDuration = unit.kind === "archer" ? 0.26 : unit.kind === "monster" ? 0.58 : unit.kind === "lancer" ? 0.44 : 0.36;
    if (unit.stateTime >= strikeDuration) {
      unit.aiMode = "retreat";
      unit.aiTimer = unit.kind === "archer" ? 0.95 : unit.kind === "monster" ? 1.2 : 0.9;
      unit.state = "walk";
      unit.stateTime = 0;
      unit.vx = 0; unit.vy = 0;
      metrics.enemyRetreatStarts += 1;
    }
    return;
  }
  if (unit.aiMode === "retreat") {
    unit.aiTimer -= dt;
    const away = normalize({ x: unit.x - hero.x, y: unit.y - hero.y });
    const side = unit.variant % 2 === 0 ? 1 : -1;
    const retreat = { x: away.x - away.y * 0.55 * side, y: away.y + away.x * 0.55 * side };
    moveCardinal(unit, retreat, unit.kind === "archer" ? 3.5 : unit.kind === "monster" ? 2.2 : 3.05, dt);
    unit.state = "walk";
    if (unit.aiTimer <= 0) { unit.aiMode = "watch"; unit.state = "idle"; unit.stateTime = 0; }
    return;
  }

  const isEngaged = unit.kind === "archer" ? engagedRangedIds.has(unit.id) : engagedMeleeIds.has(unit.id);
  if (!isEngaged) {
    unit.aiMode = "watch";
    updateWatchingEnemy(unit, dt);
    return;
  }
  unit.aiMode = "advance";
  const toHero = { x: hero.x - unit.x, y: hero.y - unit.y };
  const distance = Math.hypot(toHero.x, toHero.y);
  const direction = normalize(toHero);
  unit.facing = Math.atan2(direction.y, direction.x);
  let speed = 2.1;
  let desiredDistance = 1.65 + (unit.id % 4) * 0.48;
  if (unit.kind === "archer") { speed = 2.45; desiredDistance = isPortraitBattle() ? 3.2 : 6.2; }
  if (unit.kind === "lancer") { speed = 2.75; desiredDistance = 3.1; }
  if (unit.kind === "monster") { speed = 1.8; desiredDistance = 2.45; }
  const orbit = Math.sin(elapsed * 0.9 + unit.aiPhase) * 0.52;
  if (unit.kind === "archer" && isPortraitBattle() && (Math.abs(unit.x - hero.x) > 1.4 || unit.y < hero.y + 2.9)) {
    const lane = { x: hero.x + (unit.id % 2 ? -1.0 : 1.0), y: hero.y + 3.2 };
    moveCardinal(unit, { x: lane.x - unit.x, y: lane.y - unit.y }, speed, dt);
    unit.state = "walk";
  } else
  if (unit.kind === "archer" && distance < (isPortraitBattle() ? 2.4 : 5.2)) {
    moveCardinal(unit, { x: -direction.x + direction.y * orbit, y: -direction.y - direction.x * orbit }, speed, dt);
    unit.state = "walk";
  } else if (distance > desiredDistance) {
    moveCardinal(unit, { x: direction.x - direction.y * orbit, y: direction.y + direction.x * orbit }, speed, dt);
    unit.state = "walk";
  } else {
    const braceBeat = (elapsed + unit.aiPhase) % 2.7;
    unit.state = braceBeat < 0.38 ? "guard" : "idle";
  }

  const inView = Math.abs(unit.x - camera.position.x) < camera.right - 0.2 && Math.abs(unit.y - camera.position.y) < camera.top - 0.35;
  const attacksEnabled = elapsed - waveStartedAt > (unit.id === objectiveTargetId ? 0.8 : 1.8) && inView;
  if (attacksEnabled && unit.kind === "archer" && distance < 13 && unit.rangedCooldown <= 0 && enemyRangedBudget < 2) {
    beginEnemyWindup(unit, direction);
    enemyRangedBudget += 1;
  } else if (attacksEnabled && unit.kind === "lancer" && distance < 5 && unit.attackCooldown <= 0 && enemyMeleeBudget < 2) {
    beginEnemyWindup(unit, direction);
    enemyMeleeBudget += 1;
  } else if (attacksEnabled && unit.kind === "monster" && distance < 7 && unit.attackCooldown <= 0 && enemyMeleeBudget < 2) {
    beginEnemyWindup(unit, direction);
    enemyMeleeBudget += 1;
  } else if (attacksEnabled && unit.kind === "swordsman" && distance < 2.35 && unit.attackCooldown <= 0 && enemyMeleeBudget < 2) {
    beginEnemyWindup(unit, direction);
    enemyMeleeBudget += 1;
  }
}

function damageHero(damage: number, fromEnemyToHero: Vec2): void {
  if (hero.invulnerable > 0 || hero.state === "dead") return;
  hero.hp -= damage;
  const nudged = clampToPlayable({ x: hero.x + fromEnemyToHero.x * 0.16, y: hero.y + fromEnemyToHero.y * 0.16 });
  hero.x = nudged.x; hero.y = nudged.y;
  hero.vx = 0; hero.vy = 0; hero.vz = 0; hero.air = 0;
  hero.state = "idle";
  rhythm.hurt = 0.1;
  rhythm.recovery = Math.min(rhythm.recovery, 0.1);
  hero.stateTime = 0;
  hero.flash = 0.2;
  hero.invulnerable = 2;
  combo = 0; comboTimer = 0; spirit = Math.max(0, spirit - 4);
  shake = Math.max(shake, 0.1);
  addSpark(hero.x, hero.y, 0xff5f55, 6); audio.hit(1.2);
  if (hero.hp <= 0) finishBattle(false);
}

function updateLaunched(unit: Unit, dt: number): void {
  if (unit.state !== "launched") return;
  unit.x += unit.vx * dt;
  unit.y += unit.vy * dt;
  unit.air += unit.vz * dt;
  unit.vz -= 19.5 * dt;
  unit.vx *= Math.exp(-3.8 * dt);
  unit.vy *= Math.exp(-3.8 * dt);
  unit.mesh.rotation.z += dt * (unit.kind === "mountedBoss" ? 2.4 : unit.kind === "monster" ? 4.4 : 8.5) * (unit.id % 2 ? 1 : -1);
  const maxX = currentStage.worldWidth * 0.5;
  const maxY = currentStage.worldHeight * 0.47;
  if ((unit.x <= -maxX || unit.x >= maxX) && Math.abs(unit.vx) > 2) {
    unit.x = clamp(unit.x, -maxX, maxX);
    unit.vx = -unit.vx * 0.42;
    unit.vz = Math.max(unit.vz, 5 + unit.slamPower * 1.4);
    addImpactStar(unit.x, unit.y, 0xffd19a, 1.2 + unit.slamPower * 0.2);
    addSpark(unit.x, unit.y, 0xffa45f, 8);
    shake = Math.max(shake, 0.18 + unit.slamPower * 0.07);
  }
  if ((unit.y <= -maxY || unit.y >= maxY) && Math.abs(unit.vy) > 2) {
    unit.y = clamp(unit.y, -maxY, maxY);
    unit.vy = -unit.vy * 0.42;
    unit.vz = Math.max(unit.vz, 5 + unit.slamPower * 1.4);
    addImpactStar(unit.x, unit.y, 0xffd19a, 1.2 + unit.slamPower * 0.2);
    addSpark(unit.x, unit.y, 0xffa45f, 8);
    shake = Math.max(shake, 0.18 + unit.slamPower * 0.07);
  }
  if (unit.air > 0.35 && Math.floor(unit.stateTime * 14) !== Math.floor((unit.stateTime - dt) * 14)) addAirTrail(unit);
  if (unit.air <= 0 && unit.vz < 0) {
    unit.air = 0;
    if (Math.abs(unit.vz) > 4.4 && unit.stateTime < 1.35) {
      const impactPower = unit.slamPower;
      unit.vz = -unit.vz * (0.28 + Math.min(0.12, impactPower * 0.035));
      unit.vx *= 0.55; unit.vy *= 0.55;
      if (impactPower > 0.45 || unit.kind === "monster") addImpactStar(unit.x, unit.y, impactPower > 0.5 ? 0xffd08a : 0xe8d5a9, 0.8 + impactPower * 0.35);
      addSpark(unit.x, unit.y, impactPower > 0.5 ? 0xff744c : unit.kind === "monster" ? 0xff8055 : 0xffc987, impactPower > 0.5 ? 10 : unit.kind === "monster" ? 8 : 3);
      shake = Math.max(shake, impactPower * 0.12);
      unit.slamPower *= 0.35;
      audio.land(unit.kind === "monster" ? 1.4 : 0.8);
    } else {
      unit.vz = 0; unit.vx = 0; unit.vy = 0;
      unit.slamPower = 0;
      unit.mesh.rotation.z = 0;
      if (unit.hp <= 0 && unit.team === "enemy") {
        unit.state = "dead"; unit.stateTime = 0; ko += 1; spirit = clamp(spirit + 0.9, 0, 100);
        if (unit.id === objectiveTargetId) {
          objectiveTargetId = null;
          reinforcementTimer = Infinity;
          if (objectiveMarker) {
            scene.remove(objectiveMarker);
            (objectiveMarker.material as THREE.Material).dispose();
            objectiveMarker.geometry.dispose();
            objectiveMarker = null;
          }
          spirit = clamp(spirit + 35, 0, 100);
          routRemaining = 4.5;
          hero.hp = Math.min(hero.maxHp, hero.hp + 10);
          for (const enemy of units) {
            if (enemy.team === "enemy" && enemy.state === "idle") { enemy.state = "stunned"; enemy.stateTime = 0; enemy.flash = 0.14; }
          }
          announce("陣を突破！ 追撃ボーナス", 0.9);
        }
      } else {
        unit.state = "down"; unit.stateTime = 0;
      }
    }
  }
  unit.x = clamp(unit.x, -currentStage.worldWidth * 0.5, currentStage.worldWidth * 0.5);
  unit.y = clamp(unit.y, -currentStage.worldHeight * 0.47, currentStage.worldHeight * 0.47);
}

function updateSeparation(dt: number): void {
  const active = units.filter((unit) => unit.state !== "dead" && unit.state !== "launched");
  for (let i = 0; i < active.length; i += 1) {
    for (let j = i + 1; j < active.length; j += 1) {
      const a = active[i]; const b = active[j];
      const dx = b.x - a.x; const dy = b.y - a.y;
      const baseSeparation = (a.radius + b.radius) * (isPortraitBattle() ? 1.16 : 1.02);
      const minDistance = a.kind === "hero" || b.kind === "hero" ? Math.max(baseSeparation, isPortraitBattle() ? 1.72 : 1.25) : baseSeparation;
      const dSq = dx * dx + dy * dy;
      if (dSq > 0.0001 && dSq < minDistance * minDistance) {
        const distance = Math.sqrt(dSq);
        const push = (minDistance - distance) * Math.min(1, dt * 18) * 0.58;
        const nx = dx / distance; const ny = dy / distance;
        if (a.kind !== "hero") { a.x -= nx * push; a.y -= ny * push; }
        if (b.kind !== "hero") { b.x += nx * push; b.y += ny * push; }
      }
    }
  }
}

function updateProjectiles(dt: number): void {
  for (let i = projectiles.length - 1; i >= 0; i -= 1) {
    const projectile = projectiles[i];
    projectile.x += projectile.vx * dt;
    projectile.y += projectile.vy * dt;
    projectile.life -= dt;
    projectile.mesh.position.set(projectile.x, projectile.y + 0.6, 5);
    projectile.mesh.rotation.z = Math.atan2(projectile.vy, projectile.vx);
    let hit = false;
    if (projectile.hostile) {
      if (distanceSq(projectile, hero) < Math.pow(projectile.radius + hero.radius, 2)) {
        damageHero(projectile.damage, normalize({ x: projectile.vx, y: projectile.vy })); hit = true;
      }
    } else {
      for (const target of units) {
        if (target.team !== "enemy" || target.state === "dead") continue;
        if (distanceSq(projectile, target) < Math.pow(projectile.radius + target.radius, 2)) {
          const skill = SKILLS.find((item) => item.id === "long")!;
          if (applyHit(target, projectile.damage, normalize({ x: projectile.vx, y: projectile.vy }), projectile.launch, projectile.vertical, skill, 0, projectile.rewardsPlayer)) {
            lastAttackLaunched += 1; metrics.lastAttackLaunched = lastAttackLaunched; hit = true;
          }
        }
      }
    }
    if (projectile.life <= 0 || hit) {
      scene.remove(projectile.mesh);
      (projectile.mesh.material as THREE.Material).dispose(); projectile.mesh.geometry.dispose();
      projectiles.splice(i, 1);
    }
  }
}

function updateEffects(dt: number): void {
  for (let i = effects.length - 1; i >= 0; i -= 1) {
    const effect = effects[i];
    effect.age += dt;
    if (effect.kind === "telegraph") {
      const owner = units.find((unit) => unit.id === effect.ownerId);
      if (!owner || owner.hp <= 0 || owner.aiMode !== "windup") effect.age = effect.life;
    }
    if (effect.age < 0) continue;
    effect.mesh.visible = true;
    const progress = clamp(effect.age / effect.life, 0, 1);
    const mesh = effect.mesh as THREE.Mesh;
    const scale = lerp(effect.scaleFrom, effect.scaleTo, effect.kind === "slash" ? easeOutBack(progress) : easeOutCubic(progress));
    mesh.scale.setScalar(scale);
    if ((effect.kind === "magic" || effect.kind === "blade" || effect.kind === "hitSprite") && mesh.material) {
      const material = mesh.material as THREE.ShaderMaterial;
      const frame = Math.min(3, Math.floor(progress * 4));
      setAtlasGridFrame(material, 4, effect.kind === "magic" ? 1 : 2, effect.kind === "hitSprite" ? 1 : 0, frame);
      material.uniforms.opacity.value = progress < 0.78 ? 1 : 1 - (progress - 0.78) / 0.22;
    } else if (mesh.material) {
      const baseOpacity = effect.kind === "warning" ? 0.42 : effect.kind === "ring" ? 0.24 : effect.kind === "trail" ? 0.42 : effect.kind === "slash" ? effect.opacity ?? 0.82 : 0.88;
      (mesh.material as THREE.MeshBasicMaterial).opacity = effect.kind === "telegraph" ? 0.2 + progress * 0.45 : baseOpacity * (1 - progress);
    }
    if (effect.kind === "spark") {
      mesh.position.x += Math.sin(mesh.rotation.z) * dt * 4;
      mesh.position.y += Math.cos(mesh.rotation.z) * dt * 4 - progress * dt * 5;
    }
    if (progress >= 1) {
      if (effect.kind === "spark") activeSparkEffects = Math.max(0, activeSparkEffects - 1);
      if (effect.kind === "trail") activeTrailEffects = Math.max(0, activeTrailEffects - 1);
      scene.remove(effect.mesh);
      if (mesh.material) (mesh.material as THREE.Material).dispose();
      if (mesh.geometry) mesh.geometry.dispose();
      effects.splice(i, 1);
    }
  }
}

function runAutoplay(dt: number): void {
  if (new URLSearchParams(location.search).get("autoplay") !== "1" || !running) return;
  autoplayClock += dt;
  const nearest = horsePickup ? normalize({ x: horsePickup.x - hero.x, y: horsePickup.y - hero.y }) : nearestEnemyDirection();
  if (nearest) {
    stickVector.x = nearest.x * 0.75;
    stickVector.y = -nearest.y * 0.75;
    hero.facing = Math.atan2(nearest.y, nearest.x);
  }
  const phase = Math.floor(autoplayClock * 1.25);
  if (phase !== autoplayStep) {
    autoplayStep = phase;
    const sequence: SkillId[] = ["weak", "long", "mid", "engage", "weak", "area", "long", "weak"];
    attack(sequence[phase % sequence.length]);
  }
}

function update(dt: number): void {
  if (!running || ended || paused) return;
  rhythm.tick(dt);
  cooldowns.forEach((value, key) => cooldowns.set(key, Math.max(0, value - dt)));
  if (!clashState && (rhythm.held || rhythm.buffered > 0) && rhythm.canSlash && cooldowns.get("weak") === 0) performPrimaryAttack();
  if (freeze > 0) {
    freeze = Math.max(0, freeze - dt); updateHero(dt); updateUnitVisual(hero); updateCamera(dt, true); return;
  }
  elapsed += dt; timeLeft -= dt;
  primaryChainTimer = Math.max(0, primaryChainTimer - dt);
  if (pointerMarker) {
    const targetUnit = pointerAttackTargetId === null ? undefined : units.find((unit) => unit.id === pointerAttackTargetId && unit.state !== "dead");
    if (targetUnit) pointerMarker.position.set(targetUnit.x, targetUnit.y + 0.02, 3.8);
    const pulse = 0.9 + Math.sin(elapsed * 10) * 0.12;
    pointerMarker.scale.setScalar(pulse);
    (pointerMarker.material as THREE.MeshBasicMaterial).opacity = 0.58 + Math.sin(elapsed * 10) * 0.18;
  }
  const objectiveUnit = units.find((unit) => unit.id === objectiveTargetId && unit.state !== "dead");
  if (objectiveMarker && objectiveUnit) {
    objectiveMarker.position.set(objectiveUnit.x, objectiveUnit.y + spriteSize(objectiveUnit.kind).height + 0.22 + Math.sin(elapsed * 7) * 0.08, 6.5);
    objectiveMarker.scale.setScalar(0.85 + Math.sin(elapsed * 7) * 0.16);
  }
  if (objectiveUnit && reinforcementCount < 2) {
    reinforcementTimer -= dt;
    if (reinforcementTimer <= 0) spawnFlagReinforcement();
  }
  const flagWasUnderAttack = friendlyFlagUnderAttack;
  friendlyFlagUnderAttack = false;
  if (friendlyFlagMarker) {
    const flag = friendlyFlagPosition();
    const flagPulse = flagWasUnderAttack ? 13 : 6;
    friendlyFlagMarker.position.set(flag.x, flag.y + 0.45 + Math.sin(elapsed * flagPulse) * 0.06, 6.4);
    friendlyFlagMarker.scale.setScalar(0.9 + Math.sin(elapsed * flagPulse) * 0.12);
    const material = friendlyFlagMarker.material as THREE.MeshBasicMaterial;
    material.color.setHex(friendlyFlagHp <= 25 ? 0xff705f : friendlyFlagHp <= 60 ? 0xffd56a : 0x7ff5e8);
  }
  enemyMeleeBudget = units.filter((unit) => unit.team === "enemy" && unit.kind !== "archer" && (unit.aiMode === "windup" || unit.aiMode === "strike")).length;
  enemyRangedBudget = units.filter((unit) => unit.team === "enemy" && unit.kind === "archer" && (unit.aiMode === "windup" || unit.aiMode === "strike")).length;
  if (clashState) {
    updateClash(dt);
    if (new URLSearchParams(location.search).get("autoplay") === "1") {
      autoplayClock += dt;
      if (Math.floor(autoplayClock * 12) !== Math.floor((autoplayClock - dt) * 12)) attack("weak");
    }
    for (const unit of units) { unit.stateTime += dt; updateUnitVisual(unit); }
    updateEffects(dt);
    updatePendingHits(dt);
    updateCamera(dt, false);
    updateHud();
    return;
  }
  runAutoplay(dt);
  refreshEngagementAssignments();
  comboTimer -= dt;
  if (comboTimer <= 0) combo = 0;
  updateHero(dt);
  updateHorse(dt);
  for (const unit of units) {
    unit.stateTime += dt;
    unit.flash = Math.max(0, unit.flash - dt);
    if (unit.kind !== "hero") unit.invulnerable = Math.max(0, unit.invulnerable - dt);
    if (unit.team === "enemy") updateEnemy(unit, dt);
    else if (unit.kind !== "hero") updateAlly(unit, dt);
    if (unit.state === "stunned" && unit.stateTime > 0.65 && !pendingHits.some((pending) => pending.targetId === unit.id)) {
      unit.state = "idle";
      unit.stateTime = 0;
    }
    if (unit.state === "attack" && unit.team === "enemy" && (Math.abs(unit.vx) + Math.abs(unit.vy)) > 0.2) {
      unit.x += unit.vx * dt; unit.y += unit.vy * dt;
      unit.vx *= Math.pow(0.06, dt); unit.vy *= Math.pow(0.06, dt);
      if (distanceSq(unit, hero) < Math.pow(unit.radius + hero.radius + 0.35, 2)) {
        damageHero(unit.kind === "mountedBoss" ? 24 : unit.kind === "monster" ? 16 : 10, normalize({ x: unit.vx || hero.x - unit.x, y: unit.vy || hero.y - unit.y }));
        unit.vx = 0; unit.vy = 0;
        unit.chargePower = 0;
      }
    }
    updateLaunched(unit, dt);
    if (unit.state === "down" && unit.stateTime > (unit.kind === "monster" ? 0.82 : 0.48)) { unit.state = "idle"; unit.stateTime = 0; }
  }
  updateSeparation(dt);
  for (const unit of units) {
    const playable = clampToPlayable(unit);
    unit.x = playable.x;
    unit.y = playable.y;
    updateUnitVisual(unit);
  }
  updateProjectiles(dt);
  updateEffects(dt);
  updatePendingHits(dt);
  if (routRemaining > 0) {
    const chaseTargetVisible = units.some((unit) => unit.team === "enemy" && unit.hp > 0 && unit.state !== "dead"
      && Math.abs(unit.x - camera.position.x) < camera.right + unit.radius
      && Math.abs(unit.y + unit.air - camera.position.y) < camera.top + unit.radius);
    if (!chaseTargetVisible) routRemaining = Math.min(routRemaining, 0.45);
    routRemaining = Math.max(0, routRemaining - dt);
    if (routRemaining === 0) {
      for (const unit of units) if (unit.team === "enemy" && unit.state !== "dead" && unit.hp > 0) {
        unit.state = "dead"; unit.stateTime = 0; escapedEnemies += 1;
      }
      friendlyFlagHp = Math.min(100, friendlyFlagHp + 25);
      announce("戦線制圧 — 次の陣へ", 0.9);
    }
  }
  units = units.filter((unit) => {
    if (unit.state === "dead" && unit.stateTime > 0.92) { removeUnitVisual(unit); return false; }
    return true;
  });
  activeLaunches = units.filter((unit) => unit.team === "enemy" && unit.state === "launched").length;
  metrics.maxLaunchedAtOnce = Math.max(metrics.maxLaunchedAtOnce, activeLaunches);
  updateCamera(dt, false);
  hudTick += dt;
  if (hudTick >= 0.08) { hudTick = 0; updateHud(); }
  const waveRemaining = units.filter((unit) => unit.team === "enemy" && unit.state !== "dead").length;
  if (waveRemaining === 0) {
    waveTransition += dt;
    if (currentWaveIndex + 1 < currentStage.waves.length && waveTransition >= 1.1) {
      currentWaveIndex += 1;
      spawnWave(currentWaveIndex);
      announce(`第${currentWaveIndex + 1}波　旗頭来襲`, 0.9);
    } else if (currentWaveIndex + 1 >= currentStage.waves.length && waveTransition >= 0.8) {
      finishBattle(true);
    }
  } else {
    waveTransition = 0;
  }
  if (timeLeft <= 0) finishBattle(false);
}

function updateCamera(dt: number, frozen: boolean): void {
  cameraTarget.set(hero?.x ?? 0, (hero?.y ?? 0) + (isPortraitBattle() ? 1.0 : 0.35));
  const cameraLimitX = Math.max(0, currentStage.worldWidth * 0.5 - camera.right - 0.35);
  const cameraLimitY = Math.max(0, currentStage.worldHeight * 0.5 - camera.top - 0.35);
  cameraTarget.x = clamp(cameraTarget.x, -cameraLimitX, cameraLimitX);
  cameraTarget.y = clamp(cameraTarget.y, -cameraLimitY, cameraLimitY);
  const follow = 1 - Math.pow(0.002, dt);
  cameraPosition.lerp(cameraTarget, follow);
  shake = Math.max(0, shake - dt * 1.45);
  flashOverlay = Math.max(0, flashOverlay - dt * 2.5);
  const amount = shake * (frozen ? 0.75 : 1);
  const sx = (random() - 0.5) * amount;
  const sy = (random() - 0.5) * amount;
  camera.position.x = snapPixel(cameraPosition.x + sx);
  camera.position.y = snapPixel(cameraPosition.y + sy);
  zoomKick = Math.max(0, zoomKick - dt * 0.9);
  camera.zoom = 1;
  camera.updateProjectionMatrix();
  const vignette = document.querySelector<HTMLElement>("#vignette")!;
  vignette.style.boxShadow = flashOverlay > 0 ? `inset 0 0 ${35 + flashOverlay * 130}px rgba(255,250,180,${flashOverlay * 0.65})` : "inset 0 0 18px rgba(0,0,0,.08)";
}

function updateHud(): void {
  const status = document.querySelector<HTMLElement>("#feel-status");
  const chargeReady = rhythm.charge >= Math.max(0.32, 0.5 - runPerks.power * 0.08);
  if (status) status.textContent = rhythm.charging ? chargeReady ? "溜め完了！ 離して大薙ぎ" : `溜め ${Math.round(rhythm.charge / 0.5 * 100)}% — 離して薙ぎ払う` : rhythm.counter > 0 ? "見切り成功 — 斬で強力な反撃" : `回避 ${rhythm.charges}/2　赤い予告を見て、避けて返す`;
  document.querySelectorAll<HTMLElement>(".touch-skill").forEach((button) => {
    const id = button.dataset.skill as SkillId;
    button.classList.toggle("cooling", id === "engage" ? rhythm.charges === 0 : cooldowns.get(id)! > 0);
    button.classList.toggle("charging", id === "area" && rhythm.charging);
    if (id === "engage") button.querySelector("small")!.textContent = `回避 ${rhythm.charges}/2`;
  });
  const hpFill = document.querySelector<HTMLElement>("#hp-fill")!;
  const spiritFill = document.querySelector<HTMLElement>("#spirit-fill")!;
  hpFill.style.width = `${clamp(hero.hp / hero.maxHp * 100, 0, 100)}%`;
  spiritFill.style.width = `${spirit}%`;
  hpFill.parentElement!.querySelector("span")!.textContent = `HP ${Math.ceil(Math.max(0, hero.hp))}/${hero.maxHp}`;
  spiritFill.parentElement!.querySelector("span")!.textContent = `気 ${Math.ceil(spirit)}/100`;
  const musouReady = spirit >= MUSOU_THRESHOLD;
  document.querySelector<HTMLElement>(".bar.spirit")!.classList.toggle("musou-ready", musouReady);
  document.querySelector<HTMLElement>("#stage-name")!.textContent = `第${currentStage.id}陣 / 四陣`;
  document.querySelector<HTMLElement>("#ko-count")!.textContent = String(ko).padStart(3, "0");
  document.querySelector<HTMLElement>("#mission-title")!.textContent = routRemaining > 0 ? `突破！ あと${Math.ceil(routRemaining)}秒、追撃ボーナス` : friendlyFlagUnderAttack ? "翠旗を守れ！" : objectiveTargetId ? currentStage.description : currentStage.title;
  const remaining = units.filter((unit) => unit.team === "enemy" && unit.state !== "dead").length;
  const engaged = engagedMeleeIds.size + engagedRangedIds.size;
  const mountedStatus = mountedTime > 0 ? `　軍馬無双 ${Math.ceil(mountedTime)}秒` : horsePickup ? "　軍馬出現" : "";
  const objective = units.find((unit) => unit.id === objectiveTargetId && unit.state !== "dead");
  const compass = (dx: number, dy: number): string => Math.abs(dx) > Math.abs(dy) * 1.4 ? (dx > 0 ? "東" : "西") : Math.abs(dy) > Math.abs(dx) * 1.4 ? (dy > 0 ? "北" : "南") : `${dy > 0 ? "北" : "南"}${dx > 0 ? "東" : "西"}`;
  let objectiveStatus = "";
  if (objective) {
    const dx = objective.x - hero.x;
    const dy = objective.y - hero.y;
    objectiveStatus = `　旗頭 ${compass(dx, dy)} ${Math.ceil(Math.hypot(dx, dy))}歩`;
  }
  const flag = friendlyFlagPosition();
  const flagDx = flag.x - hero.x;
  const flagDy = flag.y - hero.y;
  const flagStatus = friendlyFlagUnderAttack ? `　翠旗 ${compass(flagDx, flagDy)} ${Math.ceil(Math.hypot(flagDx, flagDy))}歩 ${Math.ceil(friendlyFlagHp)}%` : `　翠旗 ${Math.ceil(friendlyFlagHp)}%`;
  if (friendlyFlagUnderAttack) objectiveStatus = "";
  document.querySelector<HTMLElement>("#mission-progress")!.textContent = friendlyFlagUnderAttack
    ? `第${currentWaveIndex + 1}/${currentStage.waves.length}陣${flagStatus}　撃破${ko}`
    : `第${currentWaveIndex + 1}/${currentStage.waves.length}陣　撃破 ${ko}　翠旗 ${Math.ceil(friendlyFlagHp)}%${mountedStatus || (objective ? `　旗頭${compass(objective.x-hero.x,objective.y-hero.y)} ${Math.ceil(Math.hypot(objective.x-hero.x,objective.y-hero.y))}歩` : "")}`;
  void engaged; void objectiveStatus;
  const comboElement = document.querySelector<HTMLElement>("#combo")!;
  comboElement.classList.toggle("active", combo > 1);
  comboElement.querySelector("b")!.textContent = String(combo);
  document.querySelectorAll<HTMLElement>(".skill").forEach((element) => {
    const id = element.dataset.skill as SkillId;
    const skill = SKILLS.find((item) => item.id === id)!;
    const ready = 1 - cooldowns.get(id)! / skill.cooldown;
    element.style.setProperty("--ready", String(clamp(ready, 0, 1)));
    element.classList.toggle("ready", ready >= 1);
    if (id === "area") {
      element.classList.toggle("musou-ready", musouReady);
      element.querySelector("b")!.textContent = musouReady ? "翠天無双" : "薙払";
      element.querySelector("small")!.textContent = musouReady ? "闘気解放" : "全周吹飛";
    }
  });
  const areaButton = document.querySelector<HTMLButtonElement>('.touch-skill[data-skill="area"]');
  if (areaButton && areaButton.dataset.musouReady !== String(musouReady)) {
    areaButton.dataset.musouReady = String(musouReady);
    areaButton.classList.toggle("musou-ready", musouReady);
    areaButton.querySelector<HTMLElement>(".touch-glyph")!.textContent = musouReady ? "無" : "嵐";
    areaButton.querySelector("b")!.textContent = musouReady ? "翠天無双" : "溜め薙ぎ";
    areaButton.querySelector("small")!.textContent = musouReady ? "闘気解放" : "長押し→離す";
    areaButton.setAttribute("aria-label", musouReady ? "翠天無双・闘気解放" : "千葉嵐・全周");
  }
  metrics.activeEnemies = remaining;
  metrics.ko = ko; metrics.combo = combo; metrics.lastAttackLaunched = lastAttackLaunched;
  metrics.spirit = Number(spirit.toFixed(1));
  metrics.heroX = Number(hero.x.toFixed(3)); metrics.heroY = Number(hero.y.toFixed(3));
  metrics.heroFacing = Number(hero.facing.toFixed(3)); metrics.heroFrameRow = atlasFrame(hero).row;
  metrics.effectCount = effects.length; metrics.sparkCount = activeSparkEffects; metrics.trailCount = activeTrailEffects;
  metrics.engagedMelee = engagedMeleeIds.size; metrics.engagedRanged = engagedRangedIds.size;
  metrics.watchingEnemies = Math.max(0, remaining - metrics.engagedMelee - metrics.engagedRanged);
  metrics.enemyWindups = units.filter((unit) => unit.team === "enemy" && unit.aiMode === "windup").length;
  metrics.enemyRetreating = units.filter((unit) => unit.team === "enemy" && unit.aiMode === "retreat").length;
  metrics.peakEnemyWindups = Math.max(metrics.peakEnemyWindups, metrics.enemyWindups);
  metrics.engagementLimit = meleeEngagementLimit();
  metrics.mountedSeconds = Number(mountedTime.toFixed(2));
  metrics.bossHp = Math.max(0, units.find((unit) => unit.kind === "mountedBoss")?.hp ?? 0);
  metrics.clashMeter = clashState?.meter ?? 0;
  metrics.pendingHits = pendingHits.length;
  metrics.heroHp = Math.max(0, hero.hp);
  metrics.heroInvulnerable = Number(hero.invulnerable.toFixed(2));
  metrics.reinforcementCount = reinforcementCount;
  metrics.objectiveAlive = Boolean(objective);
  metrics.objectiveDistance = objective ? Number(Math.hypot(objective.x - hero.x, objective.y - hero.y).toFixed(2)) : 0;
  metrics.friendlyFlagHp = Number(friendlyFlagHp.toFixed(1));
  metrics.friendlyFlagUnderAttack = friendlyFlagUnderAttack;
  metrics.battleEnded = ended;
  const boss = units.find((unit) => unit.id === objectiveTargetId && unit.state !== "dead");
  const bossHud = document.querySelector<HTMLElement>("#boss-hud")!;
  bossHud.classList.toggle("active", Boolean(boss));
  bossHud.querySelector("span")!.textContent = boss && (boss.state === "down" || boss.state === "launched" || boss.aiMode === "retreat") ? "崩れた！ 斬で追撃" : boss?.kind === "mountedBoss" ? "騎将・黒炎丸 — 見切りから反撃" : "旗頭の構え — 溜め・三連斬で崩す";
  bossHud.querySelector<HTMLElement>("i")!.style.width = `${boss ? clamp(boss.hp / boss.maxHp * 100, 0, 100) : 0}%`;
}

function finishBattle(success: boolean): void {
  if (ended) return;
  ended = true; running = false;
  lastBattleWon = success; rhythm.release();
  metrics.battleEnded = true;
  setTimeout(() => {
    document.querySelector<HTMLElement>("#result-title")!.textContent = success ? `${currentStage.title}　制圧` : "戦線崩壊";
    document.querySelector<HTMLElement>("#result-stats")!.innerHTML = `
      <div><b>${ko}</b><small>撃破 / 逃走${escapedEnemies}</small></div><div><b>${perfectDodgeCount}</b><small>見切り / 矢返し${reflectedArrows}</small></div><div><b>${Math.round(elapsed)}秒</b><small>戦闘時間</small></div>`;
    const next = document.querySelector<HTMLButtonElement>("#next-button")!;
    next.textContent = !success ? "この陣に再挑戦" : currentStage.id < STAGES.length ? "技を選んで次の陣へ" : "四陣制覇！ もう一度";
    document.querySelector<HTMLElement>("#reward-options")!.hidden = !success || currentStage.id === 4;
    document.querySelector("#result-screen")?.classList.add("visible");
  }, 620);
}

function announce(text: string, duration = 1.2, variant: "normal" | "musou" = "normal"): void {
  const element = document.querySelector<HTMLElement>("#announcement")!;
  element.textContent = text;
  element.classList.toggle("musou", variant === "musou");
  element.classList.add("show");
  announceTimer = duration;
}

function render(now: number): void {
  const measuredDt = Math.max(0.0001, (now - lastFrame) / 1000);
  const rawDt = Math.min(measuredDt, 0.1);
  lastFrame = now;
  maxFrameMs = Math.max(maxFrameMs, measuredDt * 1000);
  fpsAccumulator += measuredDt; fpsFrames += 1; fpsTimer += measuredDt;
  if (fpsTimer > 0.5) {
    metrics.fps = Math.round(fpsFrames / fpsAccumulator);
    metrics.frameMs = Number((1000 / Math.max(1, metrics.fps)).toFixed(2));
    metrics.maxFrameMs = Number(maxFrameMs.toFixed(2));
    fpsAccumulator = 0; fpsFrames = 0; fpsTimer = 0;
  }
  if (announceTimer > 0) {
    announceTimer -= rawDt;
    if (announceTimer <= 0) document.querySelector("#announcement")?.classList.remove("show");
  }
  const workStarted = performance.now();
  simulationAccumulator = Math.min(simulationAccumulator + rawDt, 0.1);
  while (simulationAccumulator >= 1 / 60) { update(1 / 60); simulationAccumulator -= 1 / 60; }
  const halfWidth = camera.right;
  const halfHeight = camera.top;
  lastVisibleCount = units.filter((unit) => unit.team === "enemy" && unit.state !== "dead" && Math.abs(unit.x - camera.position.x) < halfWidth + 1.2 && Math.abs(unit.y - camera.position.y) < halfHeight + 1.2).length;
  metrics.visibleEnemies = lastVisibleCount;
  metrics.peakVisibleEnemies = Math.max(metrics.peakVisibleEnemies, lastVisibleCount);
  metrics.viewportWidth = window.innerWidth;
  metrics.viewportHeight = window.innerHeight;
  metrics.cameraHalfWidth = Number(camera.right.toFixed(3));
  metrics.cameraHalfHeight = Number(camera.top.toFixed(3));
  const humanCell = logicalCellSize("hero");
  const monsterCell = logicalCellSize("monster");
  const bossCell = logicalCellSize("mountedBoss");
  const pixelPitches = [
    spriteSize("hero").width / humanCell.width, spriteSize("hero").height / humanCell.height,
    spriteSize("monster").width / monsterCell.width, spriteSize("monster").height / monsterCell.height,
    spriteSize("mountedBoss").width / bossCell.width, spriteSize("mountedBoss").height / bossCell.height,
    PIXEL_WORLD, PIXEL_WORLD,
  ];
  const minPixelPitch = Math.min(...pixelPitches);
  const maxPixelPitch = Math.max(...pixelPitches);
  metrics.pixelPitchMin = Number(minPixelPitch.toFixed(5));
  metrics.pixelPitchMax = Number(maxPixelPitch.toFixed(5));
  metrics.pixelPitchSpreadPct = Number(((maxPixelPitch / minPixelPitch - 1) * 100).toFixed(2));
  renderer.render(scene, camera);
  metrics.mainThreadMs = Number((performance.now() - workStarted).toFixed(2));
  metrics.dodgeCount = dodgeCount; metrics.perfectDodgeCount = perfectDodgeCount; metrics.counterCount = counterCount;
  metrics.dodgeCharges = rhythm.charges; metrics.charge = Number(rhythm.charge.toFixed(2));
  metrics.heroState = hero?.state ?? "idle";
  metrics.wave = currentWaveIndex + 1; metrics.waveElapsed = Number((elapsed - waveStartedAt).toFixed(1));
  metrics.battlePhase = ended ? lastBattleWon ? "won" : "lost" : routRemaining > 0 ? "rout" : "assault";
  metrics.routRemaining = Number(routRemaining.toFixed(2)); metrics.escapedEnemies = escapedEnemies; metrics.reflectedArrows = reflectedArrows;
  metrics.suppliesCollected = suppliesCollected;
  metrics.drawCalls = renderer.info.render.calls;
  metrics.triangles = renderer.info.render.triangles;
  requestAnimationFrame(render);
}

function resize(): void {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const { bufferWidth, bufferHeight, cssWidth, cssHeight } = displayGrid(width, height);
  const vertical = bufferHeight * PIXEL_WORLD;
  const horizontal = bufferWidth * PIXEL_WORLD;
  camera.left = -horizontal / 2;
  camera.right = horizontal / 2;
  camera.top = vertical / 2;
  camera.bottom = -vertical / 2;
  camera.updateProjectionMatrix();
  renderer.setPixelRatio(1);
  renderer.setSize(bufferWidth, bufferHeight, false);
  // A393px viewport must crop1px of394px, not squeeze197texels into393px.
  // The same exact integer pitch applies to every actor, terrain and VFX.
  renderer.domElement.style.width = `${cssWidth}px`;
  renderer.domElement.style.height = `${cssHeight}px`;
  void frameAspect;
}

function buildUi(): void {
  const primarySkills = SKILLS.filter((skill) => skill.id === "weak" || skill.id === "long" || skill.id === "area" || skill.id === "engage");
  const desktopPresentation: Partial<Record<SkillId, { short: string; role: string }>> = {
    weak: { short: "連斬", role: "三段自動変化" },
    long: { short: "翠雷術", role: "範囲魔法" },
    area: { short: "薙払", role: "全周吹飛" },
    engage: { short: "見切り", role: "回避・2回" },
  };
  document.querySelector<HTMLElement>("#skills")!.innerHTML = primarySkills.map((skill) => {
    const presentation = desktopPresentation[skill.id]!;
    return `<button class="skill ready" data-skill="${skill.id}"><span class="key">${skill.label}</span><b>${presentation.short}</b><small>${presentation.role}</small></button>`;
  }).join("");
  const touchPresentation: Record<SkillId, { glyph: string; short: string; role: string }> = {
    weak: { glyph: "斬", short: "連斬", role: "押し続けOK" },
    mid: { glyph: "月", short: "半月断", role: "中距離" },
    long: { glyph: "術", short: "翠雷術", role: "範囲魔法" },
    area: { glyph: "嵐", short: "溜め薙ぎ", role: "長押し→離す" },
    engage: { glyph: "避", short: "見切り", role: "回避・2回" },
    disengage: { glyph: "退", short: "霞抜け", role: "離脱" },
  };
  document.querySelector<HTMLElement>("#touch-skills")!.innerHTML = primarySkills.map((skill) => {
    const presentation = touchPresentation[skill.id];
    return `<button class="touch-skill touch-${skill.id}" data-skill="${skill.id}" aria-label="${skill.title}・${presentation.role}"><span class="touch-glyph">${presentation.glyph}</span><b>${presentation.short}</b><small>${presentation.role}</small></button>`;
  }).join("");
  document.querySelector<HTMLElement>("#stage-grid")!.innerHTML = STAGES.map((stage) => `<button class="stage-card ${stage.id === selectedStage ? "selected" : ""}" data-stage="${stage.id}" style="--tone:${stage.palette[1]};--stage-art:url('${battlefieldPaths[stage.id - 1]}')"><small>${stage.subtitle}</small><b>${stage.title}</b><span>${stage.description}</span></button>`).join("");
  document.querySelectorAll<HTMLButtonElement>(".stage-card").forEach((button) => button.addEventListener("click", () => {
    selectedStage = Number(button.dataset.stage);
    document.querySelectorAll(".stage-card").forEach((item) => item.classList.remove("selected"));
    button.classList.add("selected");
    document.querySelector<HTMLButtonElement>("#start-button")!.textContent = `${STAGES[selectedStage - 1].title}へ出撃`;
  }));
  document.querySelector("#start-button")!.addEventListener("click", () => { runPerks.flow = 0; runPerks.guard = 0; runPerks.power = 0; audio.unlock(); spawnStage(selectedStage); });
  document.querySelector("#retry-button")!.addEventListener("click", () => spawnStage(currentStage.id));
  const togglePause = (): void => {
    if (!running || ended) return;
    paused = !paused; resetInputState();
    document.querySelector("#pause-screen")!.classList.toggle("visible", paused);
  };
  document.querySelector("#pause-button")!.addEventListener("click", togglePause);
  document.querySelector("#resume-button")!.addEventListener("click", togglePause);
  window.addEventListener("keydown", (event) => { if (event.code === "Escape" && !event.repeat) togglePause(); });
  document.querySelectorAll<HTMLButtonElement>("[data-reward]").forEach((button) => button.addEventListener("click", () => {
    rewardChoice = button.dataset.reward as typeof rewardChoice;
    document.querySelectorAll("[data-reward]").forEach((element) => element.classList.toggle("selected", element === button));
  }));
  document.querySelector("#next-button")!.addEventListener("click", () => {
    if (!lastBattleWon) { spawnStage(currentStage.id); return; }
    if (currentStage.id < 4) { runPerks[rewardChoice] += 1; spawnStage(currentStage.id + 1); }
    else { runPerks.flow = 0; runPerks.guard = 0; runPerks.power = 0; spawnStage(1); }
  });
  document.querySelector("#audio-toggle")!.addEventListener("click", () => {
    audioEnabled = !audioEnabled;
    document.querySelector<HTMLElement>("#audio-toggle")!.textContent = `音 ${audioEnabled ? "ON" : "OFF"}`;
    if (audioEnabled) audio.unlock();
  });
  document.querySelectorAll<HTMLButtonElement>(".touch-skill,.skill").forEach((button) => {
    button.addEventListener("pointerdown", (event) => {
      event.preventDefault(); event.stopPropagation(); metrics.touchActions += 1;
      if (actionPointerOwners.has(button)) return;
      actionPointerOwners.set(button, event.pointerId);
      button.setPointerCapture(event.pointerId);
      button.classList.add("pressed");
      const skillId = button.dataset.skill as SkillId;
      if (skillId === "weak") { rhythm.held = true; performPrimaryAttack(); }
      else if (skillId === "area" && !clashState) beginCharge();
      else attack(skillId);
    });
    button.addEventListener("pointerup", (event) => {
      if (actionPointerOwners.get(button) !== event.pointerId) return;
      actionPointerOwners.delete(button);
      event.preventDefault(); event.stopPropagation(); button.classList.remove("pressed");
      if (button.dataset.skill === "weak") rhythm.held = keyState.has("KeyJ");
      if (button.dataset.skill === "area") releaseCharge();
    });
    const cancelAction = (event: PointerEvent): void => {
      if (actionPointerOwners.get(button) !== event.pointerId) return;
      actionPointerOwners.delete(button); button.classList.remove("pressed");
      // Losing the dodge/spell finger must not release another finger's slash.
      if (button.dataset.skill === "weak") { rhythm.held = keyState.has("KeyJ"); if (!rhythm.held) rhythm.buffered = 0; }
      if (button.dataset.skill === "area") { rhythm.charging = false; rhythm.charge = 0; }
    };
    button.addEventListener("pointercancel", cancelAction);
    button.addEventListener("lostpointercapture", cancelAction);
  });
  const stick = document.querySelector<HTMLElement>("#stick")!;
  const knob = stick.querySelector<HTMLElement>("i")!;
  const updateStick = (event: PointerEvent): void => {
    const rect = stick.getBoundingClientRect();
    const x = event.clientX - (rect.left + rect.width / 2);
    const y = event.clientY - (rect.top + rect.height / 2);
    const direction = cardinalStick.sample(x, y);
    stickVector.x = direction.x; stickVector.y = direction.y;
    knob.style.transform = `translate(${stickVector.x * 30}px,${stickVector.y * 30}px)`;
  };
  stick.addEventListener("pointerdown", (event) => { event.preventDefault(); if (stickPointer !== null) return; pointerMoveTarget = null; pointerAttackTargetId = null; cardinalStick.reset(); stickPointer = event.pointerId; metrics.stickActive = true; stick.setPointerCapture(event.pointerId); updateStick(event); });
  stick.addEventListener("pointermove", (event) => { if (event.pointerId === stickPointer) updateStick(event); });
  const releaseStick = (event: PointerEvent): void => { if (event.pointerId === stickPointer) { stickPointer = null; metrics.stickActive = false; cardinalStick.reset(); stickVector.x = 0; stickVector.y = 0; knob.style.transform = ""; } };
  stick.addEventListener("pointerup", releaseStick); stick.addEventListener("pointercancel", releaseStick); stick.addEventListener("lostpointercapture", releaseStick);
}

function pointerWorldPosition(clientX: number, clientY: number): Vec2 {
  const rect = renderer.domElement.getBoundingClientRect();
  const point = new THREE.Vector3((clientX - rect.left) / rect.width * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1, 0).unproject(camera);
  return { x: point.x, y: point.y };
}

function setPointerMarker(point: Vec2): void {
  if (pointerMarker) {
    scene.remove(pointerMarker);
    (pointerMarker.material as THREE.Material).dispose();
    pointerMarker.geometry.dispose();
  }
  const material = new THREE.MeshBasicMaterial({ color: 0xb9fff0, transparent: true, opacity: 0.78, depthWrite: false, blending: THREE.AdditiveBlending });
  pointerMarker = new THREE.Mesh(new THREE.RingGeometry(0.28, 0.38, 16), material);
  pointerMarker.position.set(point.x, point.y + 0.02, 3.8);
  scene.add(pointerMarker);
}

let lastPrimaryFieldEvent = -Infinity;

function handleFieldInput(clientX: number, clientY: number, button: number): void {
  if (!running || ended) return;
  audio.unlock();
  const point = clampToPlayable(pointerWorldPosition(clientX, clientY));
  metrics.pointerEvents += 1;
  metrics.pointerTargetX = Number(point.x.toFixed(3));
  metrics.pointerTargetY = Number(point.y.toFixed(3));
  if (button === 2) {
    metrics.pointerTargetKind = "ground";
    hero.facing = Math.atan2(point.y - hero.y, point.x - hero.x);
    attack("area");
    return;
  }
  const target = units
    .filter((unit) => unit.team === "enemy" && unit.state !== "dead")
    .map((unit) => ({ unit, distance: distanceSq(point, unit) }))
    .filter((entry) => entry.distance < Math.pow(entry.unit.radius + 1.15, 2))
    .sort((a, b) => a.distance - b.distance)[0]?.unit;
  pointerMoveTarget = target ? { x: target.x, y: target.y } : point;
  pointerAttackTargetId = target?.id ?? null;
  metrics.pointerTargetKind = target ? "enemy" : "ground";
  setPointerMarker(pointerMoveTarget);
}

renderer.domElement.addEventListener("pointerdown", (event) => {
  event.preventDefault();
  if (event.button === 0) lastPrimaryFieldEvent = performance.now();
  handleFieldInput(event.clientX, event.clientY, event.button);
});

window.addEventListener("click", (event) => {
  if (performance.now() - lastPrimaryFieldEvent < 140) return;
  const element = event.target instanceof Element ? event.target : null;
  if (element?.closest("button,#stick,#touch-skills,.panel.visible")) return;
  lastPrimaryFieldEvent = performance.now();
  handleFieldInput(event.clientX, event.clientY, 0);
}, { capture: true });

renderer.domElement.addEventListener("pointermove", (event) => {
  if (!running || ended || event.pointerType === "mouse" && (event.buttons & 1) === 0 || pointerAttackTargetId !== null) return;
  pointerMoveTarget = pointerWorldPosition(event.clientX, event.clientY);
});

window.addEventListener("keydown", (event) => {
  keyState.add(event.code);
  if (["KeyJ", "KeyK", "KeyU", "KeyI", "Space", "ShiftLeft", "ShiftRight"].includes(event.code)) event.preventDefault();
  if (event.repeat) return;
  const mapping: Partial<Record<string, SkillId>> = { KeyJ: "weak", KeyK: "mid", KeyU: "long", KeyI: "area", Space: "engage", ShiftLeft: "disengage", ShiftRight: "disengage" };
  const skill = mapping[event.code];
  if (skill === "weak") { rhythm.held = true; performPrimaryAttack(); }
  else if (skill === "area" && !clashState) beginCharge();
  else if (skill) attack(skill);
});
window.addEventListener("keyup", (event) => {
  keyState.delete(event.code);
  if (event.code === "KeyJ") rhythm.held = [...actionPointerOwners.keys()].some(button => button.dataset.skill === "weak");
  if (event.code === "KeyI") releaseCharge();
});
function resetInputState(): void {
  keyState.clear(); rhythm.release(); stickVector.x = 0; stickVector.y = 0; stickPointer = null; metrics.stickActive = false;
  pointerMoveTarget = null; pointerAttackTargetId = null;
  document.querySelector<HTMLElement>("#stick i")!.style.transform = "";
  cardinalStick.reset(); actionPointerOwners.clear();
  document.querySelectorAll('.pressed').forEach(button => button.classList.remove('pressed'));
}
window.addEventListener("blur", resetInputState);
document.addEventListener("visibilitychange", () => { if (document.hidden) resetInputState(); });
window.addEventListener("resize", resize);
window.visualViewport?.addEventListener("resize", resize);

async function boot(): Promise<void> {
  buildUi(); resize();
  const entries = Object.entries(texturePaths) as [keyof typeof texturePaths, string][];
  await Promise.all([
    ...entries.map(async ([key, path]) => { textures[key] = await loadTexture(path); }),
    ...battlefieldPaths.map(async (path, index) => { battlefieldTextures[index] = await loadTexture(path); }),
    ...["grass", "bridge", "brick", "shrine"].map(async (name, index) => { floorTextures[index] = await loadTexture(`/assets/battlefields/ground-${name}-v1.png`); }),
  ]);
  const anchorsResponse = await fetch('/assets/units-sfc/anchors-v11.json');
  if (!anchorsResponse.ok) throw new Error(`Sprite anchors unavailable: ${anchorsResponse.status}`);
  const anchorData = await anchorsResponse.json() as Record<string, number[]>;
  for (const [key, path] of entries) {
    if (key === "magicBurst" || key === "bladeFx" || key === "scenery") continue;
    const cols = key === "monster" || key === "mountedBoss" || key === "warhorse" ? 8 : 10;
    const name = path.split('/').at(-1)!.replace('-v11.png', '');
    textures[key].userData.footPivots = anchorData[name] ?? measureFootPivots(textures[key].image as HTMLImageElement, cols, key === "monster" ? 3 : 4);
  }
  // Re-read the viewport after asynchronous assets finish. Mobile emulation,
  // address-bar resizing, and orientation changes can occur during boot.
  resize();
  drawGround(STAGES[0]);
  hero = createUnit("hero", 0, 0);
  units.push(hero);
  updateUnitVisual(hero);
  window.__BLOWBACK_START__ = (stage = selectedStage) => spawnStage(stage);
  window.__BLOWBACK_DEBUG_BOSS__ = () => {
    spawnStage(4);
    for (const unit of units.filter((item) => item.kind !== "hero")) removeUnitVisual(unit);
    units = [hero];
    currentWaveIndex = currentStage.waves.length - 1;
    const boss = createUnit("mountedBoss", hero.x + 6, hero.y);
    boss.attackCooldown = 0;
    units.push(boss);
    refreshEngagementAssignments();
  };
  window.__BLOWBACK_DEBUG_HORSE__ = () => {
    horseSpawned = false;
    spawnHorsePickup();
    if (horsePickup) { horsePickup.x = hero.x; horsePickup.y = hero.y; beginMountedMode(); }
  };
  window.__BLOWBACK_DEBUG_DAMAGE__ = () => damageHero(20, { x: 1, y: 0 });
  window.__BLOWBACK_DEBUG_FEEL__ = (mode = "quiet") => {
    spawnStage(1);
    clearStageProps();
    for (const unit of units) if (unit !== hero) removeUnitVisual(unit);
    units = [hero]; objectiveTargetId = null; reinforcementTimer = Infinity;
    // Keep one distant living target so a test fixture cannot auto-complete.
    units.push(createUnit("swordsman", 9, 6));
    if (mode === "threat") {
      const enemy = createUnit("swordsman", 1.8, 0);
      enemy.aiMode = "windup"; enemy.aiTimer = 0.24; enemy.state = "guard"; enemy.vx = -6; enemy.vy = 0;
      units.push(enemy);
    }
    if (mode === "boss") {
      const boss = createUnit("mountedBoss", 2.4, 0);
      boss.aiMode = "windup"; boss.aiTimer = 0.24; boss.state = "guard"; boss.vx = -9; boss.chargePower = 1;
      units.push(boss);
    }
    if (mode === "arrow") createProjectile(1.4, 0, -3, 0, true);
    if (mode === "allyFinish") {
      const target = createUnit("swordsman", 1.5, 0); target.hp = 3;
      const ally = createUnit("swordsman", 0.7, 0, "player");
      units.push(target, ally); applyAllyHit(ally, target, 3, 0);
    }
    if (mode === "crowd") for (let i = 0; i < 20; i += 1) {
      const angle = i * Math.PI * 2 / 20;
      units.push(createUnit("swordsman", Math.cos(angle) * 2.6, Math.sin(angle) * 2.6));
    }
    updateHud();
  };
  window.__BLOWBACK_DEBUG_SAFE_INPUT__ = () => { hero.invulnerable = Math.max(hero.invulnerable, 30); };
  window.__BLOWBACK_DEBUG_MUSOU__ = (ready = true, clusterEnemies = false) => {
    spirit = ready ? MUSOU_THRESHOLD : 20;
    if (clusterEnemies) {
      const targets = units.filter((unit) => unit.team === "enemy" && unit.state !== "dead").slice(0, 18);
      targets.forEach((target, index) => {
        const ring = index < 8 ? 2.75 : 7.35;
        const ringIndex = index < 8 ? index : index - 8;
        const ringCount = index < 8 ? 8 : 10;
        const angle = ringIndex / ringCount * Math.PI * 2 + (index < 8 ? 0 : Math.PI / 10);
        const position = clampToPlayable({ x: hero.x + Math.cos(angle) * ring, y: hero.y + Math.sin(angle) * ring });
        target.x = position.x; target.y = position.y; target.vx = 0; target.vy = 0; target.vz = 0; target.air = 0;
        target.state = "idle"; target.stateTime = 0; target.invulnerable = 0;
      });
    }
    updateHud();
  };
  window.__BLOWBACK_DEBUG_FRIENDLY_FLAG__ = () => {
    damageFriendlyFlag(25);
    updateHud();
  };
  window.__BLOWBACK_DEBUG_REINFORCEMENT__ = () => {
    if (units.some((unit) => unit.id === objectiveTargetId && unit.state !== "dead")) spawnFlagReinforcement();
  };
  window.__BLOWBACK_DEBUG_OBJECTIVE__ = () => {
    const target = units.find((unit) => unit.id === objectiveTargetId && unit.state !== "dead");
    if (!target) return;
    target.invulnerable = 0;
    const skill = SKILLS.find((item) => item.id === "weak")!;
    applyHit(target, 999, normalize({ x: target.x - hero.x, y: target.y - hero.y }), 9, 7, skill, 0.8);
  };
  window.__BLOWBACK_DEBUG_SNAPSHOT__ = () => units.map((unit) => ({
    id: unit.id, kind: unit.kind, team: unit.team, state: unit.state,
    hp: unit.hp, objective: unit.id === objectiveTargetId, air: unit.air,
    x: Number(unit.x.toFixed(2)), y: Number(unit.y.toFixed(2)),
    aiMode: unit.aiMode, variant: unit.variant,
    aiTimer: unit.aiTimer,
    engaged: engagedMeleeIds.has(unit.id) || engagedRangedIds.has(unit.id),
    flagRaider: unit.team === "enemy" && unit.id % 11 === 0,
    portraitReserve: unit.team === "enemy" && unit.id % 3 !== 0,
  }));
  const startButton = document.querySelector<HTMLButtonElement>("#start-button")!;
  startButton.disabled = false;
  startButton.textContent = `${STAGES[selectedStage - 1].title}へ出撃`;
  if (new URLSearchParams(location.search).get("autoplay") === "1") spawnStage(selectedStage);
  requestAnimationFrame(render);
}

void boot().catch((error: unknown) => {
  metrics.errors.push(String(error));
  document.querySelector<HTMLElement>("#announcement")!.textContent = `起動失敗: ${String(error)}`;
  document.querySelector("#announcement")?.classList.add("show");
});
